import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { DatePickerModule } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';

import { KpiCardComponent, KpiTrend } from '../../../shared/UI/kpi-card/kpi-card.component';
import { ChartCardComponent } from '../../../shared/UI/chart-card/chart-card.component';
import { AgedDebtBucketChartComponent } from '../../../shared/charts/aged-debt-bucket-chart.component';
import { ClientRevenueHistoryChartComponent } from '../../../shared/charts/client-revenue-history-chart.component';
import { ContractorsWorkedTrendChartComponent } from '../../../shared/charts/contractors-worked-trend-chart.component';
import { DsoTrendChartComponent } from '../../../shared/charts/dso-trend-chart.component';
import { EbitdaMarginTrendChartComponent } from '../../../shared/charts/ebitda-margin-trend-chart.component';
import {
  FinancialOverviewChartComponent,
  FinancialOverviewChartClick,
  FinancialOverviewSelectionRange
} from '../../../shared/charts/financial-overview-chart.component';
import { CashFlowTrendChartComponent } from '../../../shared/charts/cash-flow-trend-chart.component';
import {
  DonutChartItem,
  EvertimeDonutChartComponent
} from '../../../shared/charts/evertime-donut-chart.component';
import {
  EvertimeGpBarChartComponent,
  EvertimeGpBarChartEvent
} from '../../../shared/charts/evertime-gp-bar-chart.component';
import { EvertimeRevenuePerConsultantComponent } from '../../../shared/charts/evertime-revenue-per-consultant.component';
import { EvertimeStatCardComponent } from '../../../shared/UI/evertime-stat-card/evertime-stat-card.component';
import { GpMarginTrendChartComponent } from '../../../shared/charts/gp-margin-trend-chart.component';
import { KpiWaterfallChartComponent, ProgressionMetricSelection } from '../../../shared/charts/kpi-waterfall-chart.component';
import { MarginComparisonChartComponent } from '../../../shared/charts/margin-comparison-chart.component';
import { OperatingCostRatioChartComponent } from '../../../shared/charts/operating-cost-ratio-chart.component';
import { OverviewTableDetailComponent } from '../../../shared/charts/overview-table-detail.component';
import { PlacementRevenueChartComponent } from '../../../shared/charts/placement-revenue-chart.component';
import {
  ColumnChartDatum,
  RevenueColumnChartComponent
} from '../../../shared/charts/revenue-column-chart.component';
import {
  RevenueRadialChartComponent,
  RadialSegmentClick,
  RadialChartDetail,
  BreakdownViewModel
} from '../../../shared/charts/revenue-radial-chart.component';
import {
  DataTableComponent,
  TableColumn
} from '../../../shared/UI/data-table/data-table.component';
import { SlicersPanelComponent } from '../../../shared/UI/slicers-panel/slicers-panel.component';
import {
  InvoiceDialogComponent,
  InvoiceDetail
} from '../../../shared/UI/invoice-dialog/invoice-dialog.component';
import {
  DashboardService,
  AgedDebtBucketItem,
  EvertimeClientPerformanceRow,
  EvertimeClientRevenueTrendPoint,
  EvertimeConsultantLeaderboardRow,
  EvertimeConsultantMonthlyPerformanceRow,
  EvertimeConsultantInvoiceTimesheetDetailRow,
  EvertimeContractorTrendPoint,
  CreditControlClientRow,
  ClientChaseListDrillDownDetailRow,
  DsoTrendItem,
  RevenueConcentrationRow,
  EvertimeGpRow,
  EvertimeGpMarginTrendPoint,
  EvertimePlacementRevenueRow,
  EvertimeRevenuePerConsultantSummary,
  EvertimeSummary,
  FinancialOverviewPoint,
  ProgressionFinancialMetric,
  CashCollectedLedgerDrillDownRow,
  AgedDebtOver90DaysDrillDownRow,
  AgedDebtBucketSummaryViewRow,
  AgedDebtBucketDetailViewRow,
  TurnoverDrillDownSummary,
  TurnoverDrillDownDetailRow,
  CostOfSalesDrillDownDetailRow,
  AdministrativeCostDrillDownSummaryRow,
  AdministrativeCostDrillDownDetailRow,
  FinancialOverviewDailyDrillDetailRow
} from '../../../services/apis/dashboard/dashboard.service';
import { XeroIntegrationService } from '../../../services/apis/xero-integration/xero-integration.service';
import { DashboardStateService } from '../../../services/dashboard-state.service';
import { CurrentAgedDebtService, CurrentAgedDebtSummary, UnallocatedCashSourceRow } from '../../../services/apis/current-aged-debt/current-aged-debt.service';

export type DashboardDrillLevel = 'year' | 'quarter' | 'month' | 'week' | 'day';

export interface BreakdownPopoverData {
  contactName: string;
  amount: string;
  percentage: string;
}

export interface KpiViewModel {
  key: string;
  label: string;
  value: string;
  icon: string;
  iconImageUrl?: string;
  status: 'positive' | 'warning' | 'critical' | 'neutral';
  trendDirection: 'up' | 'down' | 'flat' | null;
  note: string | null;
  sourceState: 'current' | 'stale' | 'unavailable' | 'loading';
  /** Short badge on the left ribbon e.g. MTD, NFI */
  ribbonLabel?: string;
  /** Secondary caption under the trend pill e.g. Month to date */
  periodLabel?: string;
  details?: Array<{ label: string; value: string }>;
  previousValue?: string;
  sparklineData?: number[];
  trend?: KpiTrend;
  comparisonValue?: string;
  comparisonPeriodLabel?: string;
}

export interface OverviewComparisonRange {
  startDate: string;
  endDate: string;
}

export type RevenueOverviewMetric = 'Revenue' | 'NFI' | 'gross_profit_margin' | 'ebitda_margin' | 'net_profit_margin';
type CreditControlBucketField = 'bucket0To30' | 'bucket31To60' | 'bucket61To90' | 'bucket90Plus';
type ClientChaseAgeBucket = 'all' | CreditControlBucketField;

/** Mirrors the bar colors in AgedDebtBucketChartComponent so the highlighted table column always matches the clicked bucket. */
const CREDIT_CONTROL_BUCKET_COLORS: Record<CreditControlBucketField, string> = {
  bucket0To30: '#12B76A',
  bucket31To60: '#F59E0B',
  bucket61To90: '#F97316',
  bucket90Plus: '#F04438',
};
export type DashboardPageVariant =
  | 'overview'
  | 'revenue'
  | 'cash-credit-control'
  | 'team-performance'
  | 'clients';

export interface AlertConfig {
  icon: string;
  title: string;
  subtitle: string;
  timestamp?: string;
  isNew?: boolean;
}

export type DashboardContextSource =
  | 'revenue'
  | 'cashFlow'
  | 'breakdown'
  | 'invoice';

export type DashboardContextAction =
  | 'drillDown'
  | 'drillUp'
  | 'keepOnly'
  | 'clearFilter'
  | 'viewRecords'
  | 'copyValue';

export interface DashboardContextPayload {
  source: DashboardContextSource;
  action: DashboardContextAction;
  index?: number;
  row?: unknown;
}

interface TurnoverWeeklyDetailRow {
  monthLabel: string;
  weekLabel: string;
  amount: number;
  amountPaid: number;
}

interface TurnoverMonthlyDetailRow {
  monthLabel: string;
  amount: number;
  amountPaid: number;
  drillable: true;
}

interface CostOfSalesMonthlyDetailRow {
  monthLabel: string;
  amount: number;
  amountPaid: number;
}

interface FinancialOverviewWeeklyDetailRow {
  monthLabel: string;
  weekLabel: string;
  kpiValue: number;
}

interface FinancialOverviewMonthlyDetailRow {
  monthLabel: string;
  kpiValue: number;
  drillable: true;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  host: {
    class: 'flex flex-col flex-1 min-h-0 overflow-hidden'
  },
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    DatePickerModule,
    TooltipModule,
    KpiCardComponent,
    ChartCardComponent,
    AgedDebtBucketChartComponent,
    ClientRevenueHistoryChartComponent,
    ContractorsWorkedTrendChartComponent,
    DsoTrendChartComponent,
    EbitdaMarginTrendChartComponent,
    FinancialOverviewChartComponent,
    CashFlowTrendChartComponent,
    EvertimeDonutChartComponent,
    EvertimeGpBarChartComponent,
    EvertimeRevenuePerConsultantComponent,
    EvertimeStatCardComponent,
    GpMarginTrendChartComponent,
    KpiWaterfallChartComponent,
    MarginComparisonChartComponent,
    OperatingCostRatioChartComponent,
    OverviewTableDetailComponent,
    PlacementRevenueChartComponent,
    RevenueColumnChartComponent,
    RevenueRadialChartComponent,
    DataTableComponent,
    SlicersPanelComponent,
    InvoiceDialogComponent
  ],
  templateUrl: './dashboard.component.html'
})
export class DashboardComponent implements OnInit, OnChanges, OnDestroy {
  private readonly currentAgedDebtService = inject(CurrentAgedDebtService);
  private readonly dashboardService = inject(DashboardService);
  private readonly xeroIntegrationService = inject(XeroIntegrationService);
  private readonly dashboardState = inject(DashboardStateService);
  private readonly subscriptions = new Subscription();

  @Input() pageTitle = '';
  @Input() pageSubtitle = '';
  @Input() pageVariant: DashboardPageVariant = 'overview';

  @Input() kpis: KpiViewModel[] = [];
  @Input() dataLoading = false;

  @Input() financialOverviewData: FinancialOverviewPoint[] | null = null;
  @Input() marginComparisonData: FinancialOverviewPoint[] = [];
  @Input() operatingCostPercentData: FinancialOverviewPoint[] = [];
  @Input() financialOverviewDrillDetail: FinancialOverviewPoint[] = [];
  @Input() placementRevenuePerMonth: EvertimePlacementRevenueRow[] = [];
  @Input() gpmPermVsTempByMonth: EvertimeGpMarginTrendPoint[] = [];
  @Input() overviewWaterfallData: FinancialOverviewPoint[] | null = null;
  @Input() progressionOfFinancialMetrics: ProgressionFinancialMetric[] = [];
  @Input() comparisonFinancialOverviewData: FinancialOverviewPoint[] | null = null;
  @Input() comparisonProgressionOfFinancialMetrics: ProgressionFinancialMetric[] = [];
  @Input() comparisonLoading = false;
  @Input() comparisonPeriodLabel: string | null = null;
  @Input() cashCollectedLedgerDrillDown: CashCollectedLedgerDrillDownRow[] = [];
  @Input() comparisonCashCollectedLedgerDrillDown: CashCollectedLedgerDrillDownRow[] = [];
  @Input() agedDebtOver90DaysDrillDown: AgedDebtOver90DaysDrillDownRow[] = [];
  @Input() agedDebtBucketSummaryView: AgedDebtBucketSummaryViewRow[] = [];
  @Input() agedDebtBucketDetailView: AgedDebtBucketDetailViewRow[] = [];
  @Input() turnoverDrillDownSummary: TurnoverDrillDownSummary | null = null;
  @Input() turnoverDrillDownDetail: TurnoverDrillDownDetailRow[] = [];
  @Input() costOfSalesDrillDownDetail: CostOfSalesDrillDownDetailRow[] = [];
  @Input() administrativeCostDrillDownSummary: AdministrativeCostDrillDownSummaryRow[] = [];
  @Input() administrativeCostDrillDownDetail: AdministrativeCostDrillDownDetailRow[] = [];
  @Input() financialOverviewDailyDrillDetail: FinancialOverviewDailyDrillDetailRow[] = [];
  @Input() comparisonFinancialOverviewDrillDetail: FinancialOverviewPoint[] = [];
  @Input() comparisonTurnoverDrillDownSummary: TurnoverDrillDownSummary | null = null;
  @Input() comparisonTurnoverDrillDownDetail: TurnoverDrillDownDetailRow[] = [];
  @Input() comparisonCostOfSalesDrillDownDetail: CostOfSalesDrillDownDetailRow[] = [];
  @Input() comparisonAdministrativeCostDrillDownSummary: AdministrativeCostDrillDownSummaryRow[] = [];
  @Input() comparisonAdministrativeCostDrillDownDetail: AdministrativeCostDrillDownDetailRow[] = [];
  @Input() comparisonFinancialOverviewDailyDrillDetail: FinancialOverviewDailyDrillDetailRow[] = [];
  @Input() overviewSelectionRange: FinancialOverviewSelectionRange | null = null;
  @Input() ebitdaFinancialOverviewData: FinancialOverviewPoint[] | null = null;
  @Input() evertimeStartDate: string | null = null;
  @Input() evertimeEndDate: string | null = null;
  @Input() breakdownChartData: unknown = null;
  @Input() breakdownDetails: RadialChartDetail[] = [];
  @Input() breakdownCenterValue = '';
  @Input() breakdownCenterLabel = '';
  @Input() trendChartData: unknown = null;
  @Input() trendChartOptions: unknown = null;
  @Input() agedDebtBuckets: AgedDebtBucketItem[] = [];
  @Input() currentAgedDebtSummary: CurrentAgedDebtSummary | null = null;
  @Input() unallocatedCashRows: UnallocatedCashSourceRow[] = [];
  unallocatedCashDetailsVisible = false;
  @Input() agedDebtAsAtLabel = '';
  @Input() agedDebtAsAtDate: string | null = null;
  @Input() currentAgedDebtLoading = false;
  @Input() dsoTrend: DsoTrendItem[] = [];
  @Input() dsoByMonthLoading = false;
  @Input() dsoTargetDays = 45;
  @Input() creditControlClients: CreditControlClientRow[] = [];
  clientChaseAgedDebtBuckets: AgedDebtBucketItem[] = [];
  @Input() clientChaseListDrillDownDetail: ClientChaseListDrillDownDetailRow[] = [];
  clientChaseNameSearch = '';
  clientChasePageSize: number | 'all' = 5;
  selectedClientChaseClient: CreditControlClientRow | null = null;
  selectedClientChaseAgeBucket: ClientChaseAgeBucket = 'all';
  clientChaseDetailsLoading = false;
  private clientChaseDetailsRequestSeq = 0;
  @Input() revenueConcentration: RevenueConcentrationRow[] = [];
  @Input() top10ClientsByGrossProfit: EvertimeGpRow[] = [];
  @Input() clientPerformanceData: EvertimeClientPerformanceRow[] = [];

  @Input() activeCrossFilter: any = null;
  @Input() revenueDrillLevel: DashboardDrillLevel = 'year';
  @Input() activeTrendLevel: DashboardDrillLevel = 'day';

  @Input() chart1Title = '';
  @Input() chart1Subtitle = '';
  @Input() chart1Breadcrumb: string[] = [];
  @Input() chart1ShowDrillUp = false;
  @Input() chart1Filtered = false;
  @Input() activeRevenueTab = 'year';
  @Input() activeChart1Metric: RevenueOverviewMetric = 'Revenue';

  get totalIncome(): number { return this.financialOverviewData?.reduce((sum, pt) => sum + pt.totalIncome, 0) ?? 0; }
  get totalExpenses(): number { return this.financialOverviewData?.reduce((sum, pt) => sum + pt.totalExpenses, 0) ?? 0; }
  get totalInvestments(): number { return this.financialOverviewData?.reduce((sum, pt) => sum + Number(pt.investments ?? 0), 0) ?? 0; }
  get totalSavings(): number { return this.financialOverviewData?.reduce((sum, pt) => sum + Number(pt.savings ?? 0), 0) ?? 0; }
  get dashboardDateRangeLabel(): string | null {
    const start = this.formatLongDate(this.evertimeStartDate);
    const end = this.formatLongDate(this.evertimeEndDate);
    if (start && end) return `${start} – ${end}`;
    return start || end;
  }

  private formatLongDate(value: string | null): string | null {
    if (!value) return null;
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return value;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  tableDialogHeader(title: string, periodOverride?: string | null): string {
    const period = periodOverride || this.dashboardDateRangeLabel;
    return period ? `${title} | ${period}` : title;
  }

  get revenueTrendData(): ColumnChartDatum[] | null {
    if (!this.financialOverviewData?.length) return null;
    return this.financialOverviewData.map(point => ({
      key: point.period,
      label: point.periodLabel || point.period,
      value: Number(point.totalIncome ?? 0),
    }));
  }

  @Input() chart2Title = '';
  @Input() chart2Subtitle = '';
  @Input() chart2Breadcrumb: string[] = [];
  @Input() chart2Filtered = false;

  @Input() chart3Title = '';
  @Input() chart3Subtitle = '';
  @Input() chart3Breadcrumb: string[] = [];
  @Input() chart3Filtered = false;

  @Input() breakdownPopoverData: BreakdownPopoverData | null = null;
  @Input() breakdownViewModel: BreakdownViewModel | null = null;
  @Input() hoveredBreakdownIndex: number | null = null;
  @Input() clientListRows: any[] = [];

  @Input() crossFilterActive = false;

  @Input() tableColumns: TableColumn[] = [];
  @Input() tableRows: any[] | null = null;
  @Input() tableStatusField?: string;

  @Input() tableColumns2: TableColumn[] = [];
  @Input() tableRows2: any[] | null = null;

  @Output() chart1BreadcrumbClick = new EventEmitter<number>();
  @Output() chart1DrillUp = new EventEmitter<void>();
  @Output() chart1DrillDown = new EventEmitter<void>();
  @Output() chart1NextLevel = new EventEmitter<void>();
  @Output() chart1ExpandAll = new EventEmitter<void>();
  @Output() chart1OpenDetails = new EventEmitter<void>();
  @Output() chart1BarClick = new EventEmitter<number>();
  @Output() ebitdaBarClick = new EventEmitter<number>();
  @Output() chart1Scroll = new EventEmitter<WheelEvent>();
  @Output() revenueLevelClick = new EventEmitter<string>();
  @Output() chart1MetricChange = new EventEmitter<string>();
  @Output() overviewSelectionRangeChange = new EventEmitter<FinancialOverviewSelectionRange>();
  @Output() overviewSelectionClear = new EventEmitter<void>();
  @Output() overviewMonthShowTable = new EventEmitter<number>();

  @Output() breakdownSegmentClick = new EventEmitter<number>();
  @Output() viewInvoicesClick = new EventEmitter<void>();
  @Output() breakdownHover = new EventEmitter<number | null>();
  @Output() breakdownClientSelectById = new EventEmitter<{
    contactId: string | null;
    contactName: string;
  }>();
  @Output() breakdownDrillThrough = new EventEmitter<{
    contactId: string | null;
    contactName: string;
  }>();

  @Output() trendLevelClick = new EventEmitter<string>();
  @Output() trendDrillUp = new EventEmitter<void>();
  @Output() trendDrillDown = new EventEmitter<void>();
  @Output() clientRowClick = new EventEmitter<string>();
  @Output() trendPointSelect = new EventEmitter<any>();

  @Output() dashboardContextAction = new EventEmitter<DashboardContextPayload>();


  slicersPanelVisible = false;
  syncRunning = false;

  evertimeSummary: EvertimeSummary | null = null;
  evertimeRevenueConcentrationDonutData: DonutChartItem[] = [];
  private revenueConcentrationChartData: DonutChartItem[] = [];
  private revenueConcentrationChartSources: unknown[] = [];

  private readonly emptyEvertimeSummary: EvertimeSummary = {
    hasData: false,
    contractorsWorked: 0,
    averageContractorMarginPct: null,
    clientsBilled: 0,
    topClientsByGrossProfit: [],
    topConsultantsByGrossProfit: [],
    placementRevenueByMonth: [],
    gpMarginTrendByMonth: [],
    contractorsWorkedTrend: [],
    contractorsWorkedDetails: [],
    contractorsWorkedMonthlyDetails: [],
    consultantLeaderboard: [],
    consultantMonthlyPerformance: [],
    consultantInvoiceTimesheetDetails: [],
    clientPerformance: [],
    clientRevenueTrend: [],
    revenuePerConsultant: null,
  };

  invoiceDialogVisible = false;
  selectedInvoice: InvoiceDetail | null = null;
  selectedKpiTable: KpiViewModel | null = null;
  selectedKpiTablePeriod: 'current' | 'comparison' = 'current';
  selectedConsultant: EvertimeConsultantLeaderboardRow | null = null;
  selectedConsultantMonth: EvertimeConsultantMonthlyPerformanceRow | null = null;
  selectedContractorsWorkedMonth: string | null = null;
  selectedContractorsWorkedPeriod: string | null = null;
  selectedCashCollectedMonth: string | null = null;
  selectedKpiColorIndex = 0;
  agedDebtTableVisible = false;
  selectedAgedDebtTableMonth: Date | null = null;
  selectedAgedDebtSummaryMonth: string | null = null;
  selectedProgressionTableMetric: ProgressionFinancialMetric | null = null;
  selectedProgressionPeriod: 'current' | 'comparison' = 'current';
  selectedWeeklyFinancialMonth: string | null = null;
  progressionTableMonthFilter: Date | null = null;
  turnoverTableMonthFilter: Date | null = null;
  selectedTurnoverSalesType: string | null = null;
  selectedTurnoverMetricName: string | null = null;
  selectedTurnoverMetricValue: number | null = null;
  selectedTurnoverMonth: string | null = null;
  selectedTurnoverDetailRows: Array<TurnoverMonthlyDetailRow | TurnoverWeeklyDetailRow> = [];
  private selectedTurnoverSourceRows: TurnoverDrillDownDetailRow[] = [];
  selectedAdministrativeCost: AdministrativeCostDrillDownSummaryRow | null = null;
  selectedAdministrativeCostDetailRows: AdministrativeCostDrillDownDetailRow[] = [];
  selectedFinancialProgressionMonth: string | null = null;
  selectedFinancialProgressionRow: { monthLabel: string; value: number | null; percentage: number | null } | null = null;
  selectedFinancialDailyDetailRows: FinancialOverviewDailyDrillDetailRow[] = [];
  private readonly revenueProgressionColumns: TableColumn[] = [
    { header: 'Name', field: 'name' },
    { header: 'Value', field: 'value' },
  ];
  private readonly turnoverWeeklyDetailColumns: TableColumn[] = [
    { header: 'Month', field: 'monthLabel' },
    { header: 'Week', field: 'weekLabel' },
    { header: 'Amount', field: 'amount' },
    { header: 'Amount Paid', field: 'amountPaid' },
  ];
  private readonly turnoverMonthlyDetailColumns: TableColumn[] = [
    { header: 'Month', field: 'monthLabel' },
    { header: 'Amount', field: 'amount' },
    { header: 'Amount Paid', field: 'amountPaid' },
  ];

  get turnoverDetailColumns(): TableColumn[] {
    return this.selectedTurnoverMonth
      ? this.turnoverWeeklyDetailColumns
      : this.turnoverMonthlyDetailColumns;
  }
  private readonly costOfSalesProgressionColumns: TableColumn[] = [
    { header: 'Month', field: 'monthLabel', width: '36%' },
    { header: 'Amount', field: 'amount', width: '32%' },
    { header: 'Amount Paid', field: 'amountPaid', width: '32%' },
  ];
  private readonly administrativeCostProgressionColumns: TableColumn[] = [
    { header: 'Administrative Cost Name', field: 'administrativeCostName' },
    { header: 'Account Code', field: 'accountCode' },
    { header: 'Total Amount', field: 'totalAmount' },
  ];
  private readonly financialMetricProgressionColumns: TableColumn[] = [
    { header: 'Month Label', field: 'monthLabel' },
    { header: 'Percentage', field: 'percentage' },
    { header: 'Value', field: 'value' },
  ];
  readonly financialDailyDetailColumns: TableColumn[] = [
    { header: 'Date', field: 'date' },
    { header: 'KPI Value', field: 'kpiValue' },
    { header: 'KPI Percent', field: 'kpiPercent' },
  ];
  readonly administrativeCostDetailColumns: TableColumn[] = [
    { header: 'Date', field: 'date' },
    { header: 'Source', field: 'source' },
    { header: 'Description', field: 'description' },
    { header: 'Reference', field: 'reference' },
    { header: 'Amount', field: 'amount' },
    { header: 'Amount Paid', field: 'amountPaid' },
  ];
  readonly agedDebtTableColumns: TableColumn[] = [
    { header: 'Month Label', field: 'monthLabel' },
    { header: '90+ Aged Debt', field: 'agedDebt90Plus' },
    { header: '90+ Aged %', field: 'aged90Percent' },
    { header: 'Cash Collection', field: 'cashCollection' },
    { header: 'Turnover', field: 'turnover' },
    { header: 'Sales Ledger Total', field: 'salesLedgerTotal' },
    { header: 'Count Back DSO', field: 'countBackDSO' },
  ];
  readonly agedDebtDetailTableColumns: TableColumn[] = [
    { header: 'Invoice #', field: 'invoiceNumber' },
    { header: 'Aged Bucket', field: 'agedBucket' },
    { header: 'Client Name', field: 'clientName' },
    { header: 'Invoice Date', field: 'invoiceDate' },
    { header: 'Due Date', field: 'dueDate' },
    { header: 'Invoice Total', field: 'invoiceTotal' },
    { header: 'Outstanding at Month End', field: 'outstandingAtMonthEnd' },
    { header: 'Days Overdue', field: 'daysOverdue' },
  ];

  get filteredAgedDebtBucketSummaryView(): AgedDebtBucketSummaryViewRow[] {
    if (!this.selectedAgedDebtTableMonth) return this.agedDebtBucketSummaryView;
    return this.agedDebtBucketSummaryView.filter(row =>
      this.monthLabelMatchesDate(row.monthLabel, this.selectedAgedDebtTableMonth!)
    );
  }

  get useCompactKpiTableDialog(): boolean {
    return !!this.selectedKpiTable && this.selectedKpiTable.key !== 'contractors_worked';
  }

  get kpiTableDialogWidth(): string {
    if (this.selectedKpiTable?.key === 'contractors_worked') {
      return this.selectedContractorsWorkedPeriod
        ? 'min(840px, calc(100vw - 32px))'
        : 'min(720px, calc(100vw - 32px))';
    }
    if (this.selectedKpiTable?.key === 'cash_collected_pct') {
      return this.selectedCashCollectedMonth
        ? 'min(1180px, calc(100vw - 32px))'
        : 'min(900px, calc(100vw - 32px))';
    }
    if (this.selectedKpiTable?.key === 'aged_debt_90_pct') return 'min(1320px, calc(100vw - 32px))';
    return 'min(1280px, calc(100vw - 32px))';
  }

  private readonly tableEnabledKpiKeys = new Set([
    'contractors_worked',
    'cash_collected_pct',
    'aged_debt_90_pct',
  ]);
  private readonly kpiTableColumnsByKey: Record<string, TableColumn[]> = {
    contractors_worked: [
      { header: 'Candidate Name', field: 'candidateName', width: '13rem' },
      { header: 'First Work Date', field: 'firstWorkDate', width: '9rem' },
      { header: 'Last Work Date', field: 'lastWorkDate', width: '9rem' },
      { header: 'Days Worked', field: 'daysWorked', width: '8rem' },
    ],
    cash_collected_pct: [
      { header: 'Month', field: 'monthLabel' },
      { header: 'Monthly Cash Collected', field: 'monthlyCashCollected' },
      { header: 'Cumulative Cash Collected', field: 'cumulativeCashCollected' },
      { header: 'Monthly Cash %', field: 'monthlyCashCollectedPercent' },
      { header: 'Cumulative Cash %', field: 'cumulativeCashCollectedPercent' },
    ],
    aged_debt_90_pct: [
      { header: 'Month', field: 'monthLabel' },
      { header: 'Monthly Revenue', field: 'monthlyRevenue' },
      { header: 'Cumulative Revenue', field: 'cumulativeRevenue' },
      { header: 'Outstanding Invoices', field: 'outstandingInvoiceCount' },
      { header: 'Outstanding at Month End', field: 'totalOutstandingAtMonthEnd' },
      { header: 'Invoices Over 90 Days', field: 'agedInvoiceCountOver90Days' },
      { header: 'Debt Over 90 Days', field: 'agedDebtOver90Days' },
      { header: 'Debt Over 90 %', field: 'agedDebtOver90Percent' },
    ],
  };

  private readonly cashCollectedTransactionColumns: TableColumn[] = [
    { header: 'Date', field: 'date' },
    { header: 'Invoice No.', field: 'documentNumber' },
    { header: 'Client', field: 'client' },
    { header: 'Reference', field: 'reference' },
    { header: 'Amount', field: 'amount' },
  ];

  contextMenu:
    | {
      source: DashboardContextSource;
      index?: number;
      row?: unknown;
      x: number;
      y: number;
    }
    | null = null;
  overviewMonthContextMenu: { index: number; x: number; y: number } | null = null;
  overviewDetailIndex: number | null = null;
  overviewDetailPeriod: 'current' | 'comparison' = 'current';

  get overviewDetailData(): FinancialOverviewPoint[] | null {
    return this.overviewDetailPeriod === 'comparison'
      ? this.comparisonFinancialOverviewData
      : this.financialOverviewData;
  }

  get overviewDetailDrillData(): FinancialOverviewPoint[] {
    return this.overviewDetailPeriod === 'comparison'
      ? this.comparisonFinancialOverviewDrillDetail
      : this.financialOverviewDrillDetail;
  }

  onFinancialOverviewChartClick(event: FinancialOverviewChartClick): void {
    if (this.pageVariant === 'overview' && this.comparisonFinancialOverviewData) {
      this.overviewDetailPeriod = event.period;
      this.overviewDetailIndex = event.dataIndex;
      return;
    }
    this.chart1BarClick.emit(event.dataIndex);
  }

  readonly skeletonKpi: KpiViewModel = {
    key: '',
    label: '',
    value: '',
    icon: '',
    status: 'neutral',
    trendDirection: null,
    note: null,
    sourceState: 'loading'
  };

  private evertimeLoadSub?: Subscription;
  private lastEvertimeRangeKey = '';
  private gpMarginTrendSource: EvertimeGpMarginTrendPoint[] | null = null;
  private gpMarginTrendChartData: Array<{
    month: string;
    category: string;
    gpmPct: number | null;
  }> = [];
  private contractorTrendSource: EvertimeContractorTrendPoint[] | null = null;
  private contractorTrendChartData: EvertimeContractorTrendPoint[] = [];
  selectedRecruiterName = 'all';
  selectedClientName = 'all';
  selectedPerformanceClientName = 'all';
  selectedAgedDebtBucket: string | null = null;

  selectedLiabilitiesMonth: Date | null = this.initialLiabilitiesMonth();
  readonly liabilitiesMaxMonth = new Date();
  liabilitiesLoading = false;
  liabilitiesError = '';
  private liabilitiesRequestSequence = 0;

  get liabilitiesAsOf(): string {
    if (!this.selectedLiabilitiesMonth) return '';
    return this.effectiveLiabilitiesDate().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  get isLiabilitiesMonthComplete(): boolean {
    if (!this.selectedLiabilitiesMonth) return false;
    const today = new Date();
    return this.selectedLiabilitiesMonth.getFullYear() < today.getFullYear()
      || (
        this.selectedLiabilitiesMonth.getFullYear() === today.getFullYear()
        && this.selectedLiabilitiesMonth.getMonth() < today.getMonth()
      );
  }

  liabilities = [
    { label: 'VAT Creditor', accountTitle: 'VAT', amount: 0 },
    { label: 'PAYE Creditor', accountTitle: 'PAYE PAYABLE', amount: 0 },
    { label: 'Pension Creditor', accountTitle: 'Pension PAYABLE', amount: 0 },
    { label: 'Corporation Tax Creditor', accountTitle: 'Provision For Corporation Tax', amount: 0 },
    { label: 'Trade Creditors', accountTitle: 'Accounts Payable', amount: 0 },
  ];

  get liabilitiesTotal(): number {
    return this.liabilities.reduce((total, liability) => total + liability.amount, 0);
  }

  readonly creditControlColumns: TableColumn[] = [
    { header: 'Client Name', field: 'clientName', width: '230px' },
    { header: 'Outstanding Invoices', field: 'outstandingInvoiceCount', width: '150px' },
    { header: 'Total Outstanding', field: 'totalOutstanding', width: '150px' },
    { header: '0 to 30', field: 'bucket0To30', width: '110px', headerColor: CREDIT_CONTROL_BUCKET_COLORS.bucket0To30 },
    { header: '31 to 60', field: 'bucket31To60', width: '110px', headerColor: CREDIT_CONTROL_BUCKET_COLORS.bucket31To60 },
    { header: '61 to 90', field: 'bucket61To90', width: '110px', headerColor: CREDIT_CONTROL_BUCKET_COLORS.bucket61To90 },
    { header: '90+', field: 'bucket90Plus', width: '100px', headerColor: CREDIT_CONTROL_BUCKET_COLORS.bucket90Plus },
  ];

  get visibleCreditControlColumns(): TableColumn[] {
    const bucketField = this.activeCreditControlBucketField;
    if (!bucketField) return this.creditControlColumns;

    const visibleFields = new Set([
      'clientName',
      'outstandingInvoiceCount',
      'totalOutstanding',
      bucketField,
    ]);

    return this.creditControlColumns.filter(column => visibleFields.has(column.field));
  }

  get activeCreditControlBucketField(): CreditControlBucketField | null {
    return this.creditControlBucketField(this.selectedAgedDebtBucket);
  }

  get activeCreditControlHighlightColor(): string | null {
    const field = this.activeCreditControlBucketField;
    return field ? CREDIT_CONTROL_BUCKET_COLORS[field] : null;
  }

  get filteredCreditControlClients(): CreditControlClientRow[] {
    const field = this.activeCreditControlBucketField;
    const nameSearch = this.clientChaseNameSearch.trim().toLocaleLowerCase();

    return this.creditControlClients
      .filter(client =>
        (!field || Number(client[field] ?? 0) !== 0) &&
        (!nameSearch || client.clientName.toLocaleLowerCase().includes(nameSearch))
      );
  }

  readonly clientChaseDrillDownColumns: TableColumn[] = [
    { header: 'Invoice Number', field: 'invoiceNumber', width: '9rem' },
    { header: 'Invoice Date', field: 'invoiceDate' },
    { header: 'Due Date', field: 'dueDate' },
    { header: 'Invoice Status', field: 'invoiceStatus', width: '9rem' },
    { header: 'Invoice Details', field: 'invoiceDetails', width: '14rem' },
    { header: 'Item Type', field: 'itemType', width: '9rem' },
    { header: 'Invoice Total', field: 'invoiceTotal' },
    { header: 'Credit Notes Applied', field: 'creditNotesApplied', width: '11rem' },
    { header: 'Overpayment Applied', field: 'overpaymentsApplied', width: '11rem' },
    { header: 'Prepayment Applied', field: 'prepaymentsApplied', width: '11rem' },
    { header: 'Outstanding Amount', field: 'outstandingAmount', width: '11rem' },
    { header: 'Unallocated Cash Amount', field: 'unallocatedCashAmount', width: '12rem' },
    { header: 'Invoice Age Days', field: 'invoiceAgeDays', width: '9rem' },
    { header: 'Days Overdue', field: 'daysOverdue' },
  ];

  readonly unallocatedCashColumns: TableColumn[] = [
    { header: 'Client', field: 'clientName', width: '12rem' },
    { header: 'Source Type', field: 'sourceType', width: '10rem' },
    { header: 'Transaction Date', field: 'transactionDate' },
    { header: 'Reference', field: 'reference', width: '10rem' },
    { header: 'Status', field: 'documentStatus', width: '8rem' },
    { header: 'Currency', field: 'sourceCurrencyCode', width: '6rem' },
    { header: 'Original Receipt', field: 'originalReceiptAmount', width: '9rem' },
    { header: 'Allocated', field: 'allocatedAmount', width: '8rem' },
    { header: 'Refunded', field: 'refundedAmount', width: '8rem' },
    { header: 'Applied / Refunded', field: 'appliedOrRefundedAmount', width: '9rem' },
    { header: 'Unallocated Cash', field: 'unallocatedCashAmount', width: '9rem' },
    { header: 'Bank Txn Type', field: 'bankTransactionType', width: '9rem' },
    { header: 'Bank Txn Date', field: 'bankTransactionDate' },
    { header: 'Bank Txn Status', field: 'bankTransactionStatus', width: '9rem' },
    { header: 'Bank Account', field: 'bankAccountName', width: '11rem' },
    { header: 'Bank Reference', field: 'bankReference', width: '10rem' },
    { header: 'Bank Txn Total', field: 'bankTransactionTotal', width: '9rem' },
    { header: 'Reconciled', field: 'isReconciled', width: '7rem' },
  ];

  readonly clientChaseAgeBuckets: { label: string; value: ClientChaseAgeBucket }[] = [
    { label: 'All', value: 'all' },
    { label: '0 to 30', value: 'bucket0To30' },
    { label: '31 to 60', value: 'bucket31To60' },
    { label: '61 to 90', value: 'bucket61To90' },
    { label: '90+', value: 'bucket90Plus' },
  ];

  /** Names the export after the client and the transaction-age bucket that produced it. */
  get clientChaseExportFileName(): string {
    const clientName = this.selectedClientChaseClient?.clientName?.trim() || 'Client';
    const bucket = this.clientChaseAgeBuckets
      .find(item => item.value === this.selectedClientChaseAgeBucket)?.label ?? 'All';
    return `${clientName} - Client Chase - ${bucket} invoice age`;
  }

  clientChaseAgeBucketColor(bucket: ClientChaseAgeBucket): string | null {
    return bucket === 'all' ? null : CREDIT_CONTROL_BUCKET_COLORS[bucket];
  }

  selectClientChaseAgeBucket(bucket: ClientChaseAgeBucket): void {
    this.selectedClientChaseAgeBucket = bucket;
    if (this.selectedClientChaseClient) {
      this.loadClientChaseDetails(this.selectedClientChaseClient);
    }
  }

  /** Invoices for the selected client, restricted to the chosen transaction-age bucket. */
  get selectedClientChaseDetailRows(): ClientChaseListDrillDownDetailRow[] {
    const contactId = this.selectedClientChaseClient?.contactId;
    if (!contactId) return [];
    const bucket = this.selectedClientChaseAgeBucket;
    return this.clientChaseListDrillDownDetail.filter(row =>
      row.contactId === contactId
      && (Number(row.outstandingAmount ?? 0) !== 0 || Number(row.unallocatedCashAmount ?? 0) !== 0)
      && (bucket === 'all' || (row.itemType !== 'UNALLOCATED_CASH' && this.clientChaseAgeBucketForRow(row) === bucket))
    );
  }

  private clientChaseAgeBucketForRow(row: ClientChaseListDrillDownDetailRow): CreditControlBucketField {
    const invoiceAge = Number(row.invoiceAgeDays ?? 0);
    if (invoiceAge <= 30) return 'bucket0To30';
    if (invoiceAge <= 60) return 'bucket31To60';
    if (invoiceAge <= 90) return 'bucket61To90';
    return 'bucket90Plus';
  }

  openClientChaseDetails(client: CreditControlClientRow): void {
    this.selectedClientChaseClient = client;
    // Carry over the aged-debt bucket the user drilled in from, otherwise show everything.
    this.selectedClientChaseAgeBucket = this.activeCreditControlBucketField ?? 'all';
    this.loadClientChaseDetails(client);
  }

  private loadClientChaseDetails(client: CreditControlClientRow): void {
    if (!client.contactId) {
      this.clientChaseListDrillDownDetail = [];
      return;
    }

    const selectedBucket = this.clientChaseApiBucket(this.selectedClientChaseAgeBucket);
    const requestSeq = ++this.clientChaseDetailsRequestSeq;
    this.clientChaseDetailsLoading = true;
    this.clientChaseListDrillDownDetail = [];
    this.subscriptions.add(
      this.currentAgedDebtService
        .getClientChaseDrilldown(client.contactId, selectedBucket, this.agedDebtAsAtDate)
        .subscribe({
          next: rows => {
            if (requestSeq !== this.clientChaseDetailsRequestSeq || this.selectedClientChaseClient?.contactId !== client.contactId) return;
            this.clientChaseDetailsLoading = false;
            this.clientChaseListDrillDownDetail = rows;
          },
          error: error => {
            if (requestSeq !== this.clientChaseDetailsRequestSeq || this.selectedClientChaseClient?.contactId !== client.contactId) return;
            this.clientChaseDetailsLoading = false;
            this.clientChaseListDrillDownDetail = [];
            console.error('Failed to load Client Chase List drilldown:', error);
          },
        })
    );
  }

  private clientChaseApiBucket(bucket: ClientChaseAgeBucket): string {
    const bucketMap: Record<ClientChaseAgeBucket, string> = {
      all: 'ALL',
      bucket0To30: '0-30',
      bucket31To60: '31-60',
      bucket61To90: '61-90',
      bucket90Plus: '90+',
    };
    return bucketMap[bucket];
  }

  closeClientChaseDetails(): void {
    this.selectedClientChaseClient = null;
    this.selectedClientChaseAgeBucket = 'all';
  }

  selectAgedDebtBucket(bucket: AgedDebtBucketItem): void {
    this.selectedAgedDebtBucket =
      this.selectedAgedDebtBucket === bucket.bucket ? null : bucket.bucket;
  }

  clearAgedDebtBucketFilter(): void {
    this.selectedAgedDebtBucket = null;
  }

  private creditControlBucketField(bucket: string | null): CreditControlBucketField | null {
    const normalizedBucket = (bucket ?? '')
      .toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[–—]/g, '-');

    const canonicalBucket = normalizedBucket.replace(/[\u2013\u2014]/g, '-');

    if (/^0(?:-|to)30(?:days?)?$/.test(canonicalBucket)) return 'bucket0To30';
    if (/^31(?:-|to)60(?:days?)?$/.test(canonicalBucket)) return 'bucket31To60';
    if (/^61(?:-|to)90(?:days?)?$/.test(canonicalBucket)) return 'bucket61To90';
    if (/^(?:90\+|90plus|over90|>90)(?:days?)?$/.test(canonicalBucket)) return 'bucket90Plus';
    return null;
  }

  readonly teamLeaderboardColumns: TableColumn[] = [
    { header: 'Consultant', field: 'consultant' },
    { header: 'Revenue', field: 'revenue' },
    { header: 'Gross Profit', field: 'grossProfit' },
    { header: 'Timesheets', field: 'timesheets' },
    { header: 'Avg Margin', field: 'avgMargin' },
  ];

  readonly consultantMonthlyColumns: TableColumn[] = [
    { header: 'Month Label', field: 'monthLabel' },
    { header: 'Revenue', field: 'revenue' },
    { header: 'Gross Profit', field: 'grossProfit' },
    { header: 'Timesheets', field: 'timesheets' },
    { header: 'Avg Margin', field: 'avgMargin' },
  ];

  readonly consultantInvoiceTimesheetColumns: TableColumn[] = [
    { header: 'Invoice Date', field: 'invoiceDate' },
    { header: 'Invoice Number', field: 'invoiceNumber' },
    { header: 'Invoice Status ID', field: 'invoiceStatusId' },
    { header: 'Invoice Net Value', field: 'invoiceNetValue' },
    { header: 'Invoice VAT', field: 'invoiceVAT' },
    { header: 'Invoice Gross Value', field: 'invoiceGrossValue' },
    { header: 'Invoice Line Count', field: 'consultantInvoiceLineCount' },
    { header: 'Invoice Timesheet Count', field: 'consultantInvoiceTimesheetCount' },
    { header: 'Allocated Revenue', field: 'consultantInvoiceAllocatedRevenue' },
    { header: 'Allocated Purchase Cost', field: 'consultantInvoiceAllocatedPurchaseCost' },
    { header: 'Allocated Gross Profit', field: 'consultantInvoiceAllocatedGrossProfit' },
    { header: 'Invoice Margin %', field: 'consultantInvoiceMarginPercent' },
    { header: 'Invoice Description', field: 'invoiceDescription' },
    { header: 'Expense', field: 'isExpense' },
    { header: 'Timesheet ID', field: 'timesheetId' },
    { header: 'Candidate Name', field: 'candidateName' },
    { header: 'Client Name', field: 'clientName' },
    { header: 'Placement ID', field: 'placementId' },
    { header: 'Contract Type', field: 'contractType' },
    { header: 'Timesheet Status', field: 'timesheetStatus' },
    { header: 'Period End Date', field: 'periodEndDate' },
    { header: 'Reconcile Date', field: 'reconcileDate' },
    { header: 'Job Title', field: 'jobTitle' },
    { header: 'PO Ref', field: 'poRef' },
    { header: 'Invoice Line Revenue', field: 'invoiceLineRevenue' },
    { header: 'Timesheet Revenue', field: 'timesheetRevenue' },
    { header: 'Consultant Share %', field: 'consultantSharePercent' },
    { header: 'Line Allocated Revenue', field: 'allocatedRevenue' },
    { header: 'Line Purchase Cost', field: 'linePurchaseCost' },
    { header: 'Line Allocated Purchase Cost', field: 'allocatedPurchaseCost' },
    { header: 'Line Allocated Gross Profit', field: 'allocatedGrossProfit' },
    { header: 'Margin %', field: 'marginPercent' },
    { header: 'Timesheet Purchase Cost', field: 'timesheetPurchaseCost' },
    { header: 'Timesheet Gross Profit', field: 'timesheetGrossProfit' },
  ];

  readonly clientPerformanceColumns: TableColumn[] = [
    { header: 'Client', field: 'client', width: '12rem' },
    { header: 'Status', field: 'contactStatus', width: '6.5rem' },
    { header: 'Revenue', field: 'revenue', width: '8rem' },
    { header: 'Revenue Invoices', field: 'revenueInvoiceCount', width: '9rem' },
    { header: 'Gross Profit', field: 'grossProfit', width: '8rem' },
    { header: 'GP %', field: 'gpPct', width: '5rem' },
    { header: 'Outstanding Balance', field: 'outstandingBalance', width: '10rem' },
    { header: 'Outstanding Invoices', field: 'outstandingInvoiceCount', width: '10rem' },
    { header: 'Revenue Concentration %', field: 'revenueConcentrationPercent', width: '11rem' },
  ];

  ngOnInit(): void {
    this.bindDashboardState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['pageVariant'] && this.pageVariant !== 'overview') {
      this.closeOverviewDetail();
    }

    if (changes['financialOverviewData'] && this.overviewDetailIndex !== null) {
      const rows = this.financialOverviewData ?? [];
      this.overviewDetailIndex = rows.length > 0
        ? Math.min(this.overviewDetailIndex, rows.length - 1)
        : null;
    }

    if (changes['creditControlClients']) {
      this.rebuildClientChaseAgedDebtBuckets();
    }

    if (!changes['evertimeStartDate'] && !changes['evertimeEndDate']) return;

    const rangeKey = `${this.evertimeStartDate ?? ''}|${this.evertimeEndDate ?? ''}`;
    if (rangeKey === this.lastEvertimeRangeKey && this.evertimeSummary) return;
    this.loadEvertimeSummary();
  }

  private rebuildClientChaseAgedDebtBuckets(): void {
    if (this.creditControlClients.length === 0) {
      this.clientChaseAgedDebtBuckets = [];
      this.selectedAgedDebtBucket = null;
      return;
    }

    const totals = this.creditControlClients.reduce(
      (sum, client) => ({
        bucket0To30: sum.bucket0To30 + Number(client.bucket0To30 ?? 0),
        bucket31To60: sum.bucket31To60 + Number(client.bucket31To60 ?? 0),
        bucket61To90: sum.bucket61To90 + Number(client.bucket61To90 ?? 0),
        bucket90Plus: sum.bucket90Plus + Number(client.bucket90Plus ?? 0),
      }),
      { bucket0To30: 0, bucket31To60: 0, bucket61To90: 0, bucket90Plus: 0 }
    );

    this.clientChaseAgedDebtBuckets = [
      { bucket: '0-30', amount: totals.bucket0To30 },
      { bucket: '31-60', amount: totals.bucket31To60 },
      { bucket: '61-90', amount: totals.bucket61To90 },
      { bucket: '90+', amount: totals.bucket90Plus },
    ];
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get evertimeDisplaySummary(): EvertimeSummary {
    return this.evertimeSummary ?? this.emptyEvertimeSummary;
  }

  get evertimeRevenuePerConsultant(): EvertimeRevenuePerConsultantSummary | null {
    const summary = this.evertimeDisplaySummary.revenuePerConsultant;
    if (!summary || this.selectedRecruiterName === 'all') return summary;

    const breakdown = summary.breakdown.filter(row => row.name === this.selectedRecruiterName);
    if (!breakdown.length) return null;
    const totalRevenue = breakdown.reduce((sum, row) => sum + Number(row.revenue ?? 0), 0);
    return {
      ...summary,
      breakdown,
      totalRevenue,
      recruiterCount: breakdown.length,
      revenuePerRecruiter: totalRevenue / breakdown.length,
      currencyCode: breakdown[0]?.currencyCode ?? summary.currencyCode,
    };
  }

  get evertimeTopClients(): EvertimeGpRow[] {
    return this.top10ClientsByGrossProfit.length > 0
      ? this.top10ClientsByGrossProfit
      : this.evertimeSummary?.topClientsByGrossProfit ?? [];
  }

  get clientsTabTopClients(): EvertimeGpRow[] {
    return this.evertimeTopClients;
  }

  selectTopGrossProfitClient(event: EvertimeGpBarChartEvent): void {
    this.selectedPerformanceClientName =
      this.selectedPerformanceClientName === event.row.name ? 'all' : event.row.name;
  }

  clearSelectedPerformanceClient(): void {
    this.selectedPerformanceClientName = 'all';
  }

  get clientPerformanceRows(): EvertimeClientPerformanceRow[] {
    const performanceRows = this.clientPerformanceData.length > 0
      ? this.clientPerformanceData
      : this.evertimeSummary?.clientPerformance ?? [];
    const rows = performanceRows.length > 0
      ? performanceRows
      : this.fallbackClientPerformanceRows();

    return this.selectedPerformanceClientName === 'all'
      ? rows
      : rows.filter(row => row.client === this.selectedPerformanceClientName);
  }

  private fallbackClientPerformanceRows(): EvertimeClientPerformanceRow[] {
    const gpRows = [...this.evertimeTopClients]
      .filter(row => Number(row.revenue) > 0 || Number(row.grossProfit) > 0)
      .sort((a, b) => Number(b.grossProfit) - Number(a.grossProfit));

    if (gpRows.length > 0) {
      return gpRows.map(row => ({
        client: row.name,
        revenue: Number(row.revenue ?? 0),
        grossProfit: Number(row.grossProfit ?? 0),
        gpPct: Number(row.revenue) > 0 ? (Number(row.grossProfit ?? 0) / Number(row.revenue)) * 100 : null,
        placements: 0,
        contractors: 0,
        outstandingBalance: 0,
        currencyCode: row.currencyCode ?? null,
      }));
    }

    return [...(this.clientListRows ?? [])]
      .filter(row => Number(row.value) > 0)
      .sort((a, b) => Number(b.value) - Number(a.value))
      .map(row => ({
        client: row.name,
        revenue: Number(row.value ?? 0),
        grossProfit: 0,
        gpPct: null,
        placements: 0,
        contractors: 0,
        outstandingBalance: 0,
        currencyCode: 'GBP',
      }));
  }

  get clientRevenueConcentrationData(): DonutChartItem[] {
    const sources = [
      this.revenueConcentration,
      this.evertimeSummary,
      this.creditControlClients,
      this.clientListRows,
    ];
    const sourcesUnchanged =
      sources.length === this.revenueConcentrationChartSources.length &&
      sources.every((source, index) => source === this.revenueConcentrationChartSources[index]);

    if (sourcesUnchanged) {
      return this.revenueConcentrationChartData;
    }

    this.revenueConcentrationChartSources = sources;

    if (this.revenueConcentration.length > 0) {
      const namedClients: DonutChartItem[] = this.revenueConcentration.map(row => ({
        name: row.clientName,
        value: row.revenue,
        rank: row.rank,
        contactId: row.contactId,
        invoiceCount: row.invoiceCount,
        percentage: row.revenueConcentrationPercent,
        grossProfit: row.grossProfit,
        grossProfitMarginPercent: row.grossProfitMarginPercent,
        totalCompanyRevenue: row.totalCompanyRevenue,
        useDistinctColor: true,
      }));
      const displayedRevenue = namedClients.reduce((sum, item) => sum + item.value, 0);
      const totalCompanyRevenue = Math.max(
        displayedRevenue,
        ...this.revenueConcentration.map(row => Number(row.totalCompanyRevenue) || 0),
      );
      const otherRevenue = Math.max(0, totalCompanyRevenue - displayedRevenue);
      const namedContactIds = new Set(this.revenueConcentration.map(row => row.contactId));
      const namedClientNames = new Set(
        this.revenueConcentration.map(row => row.clientName.trim().toLocaleLowerCase()),
      );
      const otherClientRows = (this.evertimeSummary?.clientPerformance ?? []).filter(row =>
        !((row.contactId && namedContactIds.has(row.contactId)) ||
          namedClientNames.has(row.client.trim().toLocaleLowerCase()))
      );
      const apiOtherGrossProfit = this.revenueConcentration.find(
        row => row.otherGrossProfit != null,
      )?.otherGrossProfit;
      const otherGrossProfit = apiOtherGrossProfit == null
        ? otherClientRows.reduce((sum, row) => sum + Number(row.grossProfit ?? 0), 0)
        : Number(apiOtherGrossProfit);
      const apiOtherGrossProfitMargin = this.revenueConcentration.find(
        row => row.otherGrossProfitMarginPercent != null,
      )?.otherGrossProfitMarginPercent;
      const otherGrossProfitMarginPercent = apiOtherGrossProfitMargin == null
        ? (otherRevenue > 0 ? (otherGrossProfit / otherRevenue) * 100 : null)
        : Number(apiOtherGrossProfitMargin);
      const percentageOfTotal = (value: number) => totalCompanyRevenue > 0
        ? (value / totalCompanyRevenue) * 100
        : 0;

      this.revenueConcentrationChartData = namedClients.map(item => ({
        ...item,
        percentage: percentageOfTotal(item.value),
      }));
      if (otherRevenue > 0.005) {
        this.revenueConcentrationChartData.push({
          name: 'Others',
          value: otherRevenue,
          percentage: percentageOfTotal(otherRevenue),
          grossProfit: otherGrossProfit,
          grossProfitMarginPercent: otherGrossProfitMarginPercent,
          color: '#94a3b8',
        });
      }
      return this.revenueConcentrationChartData;
    }

    const rows = [...(this.evertimeSummary?.clientPerformance ?? [])]
      .filter(row => Number(row.revenue) > 0)
      .sort((a, b) => Number(b.revenue) - Number(a.revenue));

    if (rows.length === 0 && this.creditControlClients.length > 0) {
      const chaseRows = [...this.creditControlClients]
        .filter(row => Number(row.billedLedgerAmount) > 0)
        .sort((a, b) => Number(b.billedLedgerAmount) - Number(a.billedLedgerAmount));
      const top = chaseRows.slice(0, 5).map(row => ({
        name: row.clientName,
        value: Number(row.billedLedgerAmount),
      }));
      const otherValue = chaseRows
        .slice(5)
        .reduce((sum, row) => sum + Number(row.billedLedgerAmount ?? 0), 0);
      this.revenueConcentrationChartData =
        otherValue > 0 ? [...top, { name: 'Others', value: otherValue, color: '#94a3b8' }] : top;
      return this.revenueConcentrationChartData;
    }

    if (rows.length === 0 && this.clientListRows?.length) {
      const fallbackRows = [...this.clientListRows]
        .filter(row => Number(row.value) > 0)
        .sort((a, b) => Number(b.value) - Number(a.value));
      const top = fallbackRows.slice(0, 5).map(row => ({ name: row.name, value: Number(row.value) }));
      const otherValue = fallbackRows.slice(5).reduce((sum, row) => sum + Number(row.value ?? 0), 0);
      this.revenueConcentrationChartData =
        otherValue > 0 ? [...top, { name: 'Others', value: otherValue, color: '#94a3b8' }] : top;
      return this.revenueConcentrationChartData;
    }

    const top = rows.slice(0, 5).map(row => ({ name: row.client, value: Number(row.revenue) }));
    const otherValue = rows.slice(5).reduce((sum, row) => sum + Number(row.revenue ?? 0), 0);
    this.revenueConcentrationChartData =
      otherValue > 0 ? [...top, { name: 'Others', value: otherValue, color: '#94a3b8' }] : top;
    return this.revenueConcentrationChartData;
  }

  get selectedClientRevenueHistory(): EvertimeClientRevenueTrendPoint[] {
    if (this.selectedClientName === 'all') return [];

    const rows = this.evertimeSummary?.clientRevenueTrend ?? [];
    const matching = rows.filter(row => row.client === this.selectedClientName);
    const buckets = new Map<string, EvertimeClientRevenueTrendPoint>();

    for (const row of matching) {
      const existing = buckets.get(row.month);
      buckets.set(row.month, {
        month: row.month,
        monthLabel: row.monthLabel,
        client: row.client,
        revenue: (existing?.revenue ?? 0) + Number(row.revenue ?? 0),
        currencyCode: row.currencyCode ?? existing?.currencyCode ?? null,
      });
    }

    return Array.from(buckets.values()).sort((a, b) => a.month.localeCompare(b.month));
  }

  get clientRightChartTitle(): string {
    return this.selectedClientName === 'all' ? 'Revenue Concentration' : 'Monthly Revenue History';
  }

  get clientRightChartFiltered(): boolean {
    return this.selectedClientName !== 'all';
  }

  get evertimeTopConsultants(): EvertimeGpRow[] {
    return this.evertimeSummary?.topConsultantsByGrossProfit ?? [];
  }

  selectTopGrossProfitConsultant(event: EvertimeGpBarChartEvent): void {
    this.selectedRecruiterName =
      this.selectedRecruiterName === event.row.name ? 'all' : event.row.name;
  }

  clearSelectedConsultant(): void {
    this.selectedRecruiterName = 'all';
  }

  get teamContractorsWorkedTrend(): EvertimeContractorTrendPoint[] {
    const rows = this.evertimeSummary?.contractorsWorkedTrend ?? [];
    if (rows === this.contractorTrendSource) {
      return this.contractorTrendChartData;
    }

    this.contractorTrendSource = rows;

    const allConsultantsRows = rows.filter(row => row.consultantName === 'All Consultants');
    if (allConsultantsRows.length > 0) {
      this.contractorTrendChartData = allConsultantsRows;
      return this.contractorTrendChartData;
    }

    const buckets = new Map<string, EvertimeContractorTrendPoint>();
    for (const row of rows) {
      const existing = buckets.get(row.period);
      buckets.set(row.period, {
        period: row.period,
        periodLabel: row.periodLabel,
        consultantName: 'All Consultants',
        contractorsWorked: (existing?.contractorsWorked ?? 0) + Number(row.contractorsWorked ?? 0),
      });
    }

    this.contractorTrendChartData =
      Array.from(buckets.values()).sort((a, b) => a.period.localeCompare(b.period));
    return this.contractorTrendChartData;
  }

  get teamLeaderboardRows(): EvertimeConsultantLeaderboardRow[] {
    const rows = this.evertimeSummary?.consultantLeaderboard ?? [];
    return this.selectedRecruiterName === 'all'
      ? rows
      : rows.filter(row => row.consultant === this.selectedRecruiterName);
  }

  get evertimeMonthlyRevenue(): EvertimePlacementRevenueRow[] {
    return this.placementRevenuePerMonth.length > 0
      ? this.placementRevenuePerMonth
      : this.evertimeSummary?.placementRevenueByMonth ?? [];
  }

  openConsultantDialog(consultant: EvertimeConsultantLeaderboardRow): void {
    this.selectedConsultant = consultant;
  }

  closeConsultantDialog(): void {
    this.selectedConsultant = null;
    this.selectedConsultantMonth = null;
  }

  openConsultantMonthDialog(month: EvertimeConsultantMonthlyPerformanceRow): void {
    this.selectedConsultantMonth = month;
  }

  closeConsultantMonthDialog(): void {
    this.selectedConsultantMonth = null;
  }

  get selectedConsultantMonthlyRows(): EvertimeConsultantMonthlyPerformanceRow[] {
    if (!this.selectedConsultant) return [];
    const selectedName = this.selectedConsultant.consultant.trim().toLocaleLowerCase();
    return this.evertimeDisplaySummary.consultantMonthlyPerformance
      .filter(row => row.consultant.trim().toLocaleLowerCase() === selectedName);
  }

  get selectedConsultantSummaryItems(): Array<{ label: string; value: string }> {
    if (!this.selectedConsultant) return [];
    return [
      { label: 'Total Revenue', value: this.formatConsultantCurrency(this.selectedConsultant.revenue, this.selectedConsultant.currencyCode) },
      { label: 'Gross Profit', value: this.formatConsultantCurrency(this.selectedConsultant.grossProfit, this.selectedConsultant.currencyCode) },
      { label: 'Timesheets', value: String(this.selectedConsultant.timesheets) },
    ];
  }

  get selectedConsultantMonthSummaryItems(): Array<{ label: string; value: string }> {
    if (!this.selectedConsultantMonth) return [];
    return [
      { label: 'Revenue', value: this.formatConsultantCurrency(this.selectedConsultantMonth.revenue, this.selectedConsultantMonth.currencyCode) },
      { label: 'Gross Profit', value: this.formatConsultantCurrency(this.selectedConsultantMonth.grossProfit, this.selectedConsultantMonth.currencyCode) },
      { label: 'Timesheets', value: String(this.selectedConsultantMonth.timesheets) },
    ];
  }

  formatConsultantCurrency(value: number, currencyCode: string | null): string {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currencyCode || 'GBP',
      maximumFractionDigits: 0,
    }).format(value);
  }

  get evertimeGpMarginTrendData(): { month: string; category: string; gpmPct: number | null }[] {
    const rows = this.gpmPermVsTempByMonth.length > 0
      ? this.gpmPermVsTempByMonth
      : this.evertimeSummary?.gpMarginTrendByMonth ?? [];

    if (rows !== this.gpMarginTrendSource) {
      this.gpMarginTrendSource = rows;
      this.gpMarginTrendChartData = rows.flatMap(row => {
        if (row.category) {
          return [{
            month: row.monthLabel || row.month,
            category: row.category,
            gpmPct: row.gpmPct,
          }];
        }

        const raw = row as EvertimeGpMarginTrendPoint & {
          revenueAccountGpmPercentages?: Record<string, number | null>;
        };
        const percentages = raw.revenueAccountGpmPercentages;
        if (!percentages) return [];

        return Object.entries(percentages).map(([accountName, value]) => ({
          month: row.monthLabel || row.month,
          category: accountName
            .replace(/\s+GPM\s*%\s*$/i, '')
            .replace(/\s*\([^()]+\)\s*$/, '')
            .trim() || accountName,
          gpmPct: value,
        }));
      });
    }

    return this.gpMarginTrendChartData;
  }

  private formatKpiCurrency(value: number | null | undefined, currency = 'GBP'): string {
    if (value == null || !Number.isFinite(Number(value))) return '—';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(Number(value));
  }

  private formatKpiPercent(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(Number(value))) return '—';
    return `${Number(value).toFixed(1)}%`;
  }

  private kpiCard(
    key: string,
    label: string,
    value: string,
    icon: string,
    ribbonLabel: string,
    note: string | null = null
  ): KpiViewModel {
    const hasValue = value !== '—';
    return {
      key,
      label,
      value,
      icon,
      status: 'neutral',
      trendDirection: null,
      note,
      sourceState: hasValue ? 'current' : 'unavailable',
      ribbonLabel,
      periodLabel: 'Current period'
    };
  }

  formatEvertimePercent(value: number | null | undefined, hasData = true): string {
    if (!hasData || value == null) return '—';
    return `${Number(value).toFixed(1)}%`;
  }

  formatEvertimeCount(value: number | null | undefined, hasData = true): string {
    if (!hasData || value == null || !Number.isFinite(Number(value))) return '—';
    return String(value);
  }

  gaugeDash(value: number | null | undefined): string {
    const pct = value == null ? 0 : Math.max(0, Math.min(100, Number(value) || 0));
    return `${pct} 100`;
  }

  evertimeClientsBilled(summary: EvertimeSummary): number {
    const apiValue = Number((summary as any).clientsBilled);
    if (Number.isFinite(apiValue) && apiValue > 0) {
      return apiValue;
    }

    return new Set(
      (summary.topClientsByGrossProfit ?? [])
        .map(row => (row.name ?? '').trim())
        .filter(Boolean)
    ).size;
  }

  evertimeContractorsProgress(summary: EvertimeSummary): number {
    return Math.max(8, Math.min(100, Math.round((summary.contractorsWorked / 50) * 100)));
  }

  evertimeClientsProgress(summary: EvertimeSummary): number {
    const billed = this.evertimeClientsBilled(summary);
    return Math.max(8, Math.min(100, Math.round((billed / 25) * 100)));
  }

  evertimeMarginProgress(summary: EvertimeSummary): number {
    const value = summary.averageContractorMarginPct;
    return value == null ? 0 : Math.max(0, Math.min(100, Number(value)));
  }

  get kpiSlots(): KpiViewModel[] {
    const visibleKpiKeys = new Set(this.kpiKeysForPageVariant());
    const source = this.kpis.length > 0
      ? this.kpis.filter(k => visibleKpiKeys.has(k.key))
      : Array.from({ length: this.kpiSkeletonCount }, () => this.skeletonKpi);

    if (this.pageVariant === 'team-performance') {
      return this.teamPerformanceKpis;
    }

    if (this.pageVariant === 'clients') {
      return this.clientKpis;
    }

    return source;
  }

  get kpiGridClass(): string {
    if (this.pageVariant === 'cash-credit-control') {
      return 'dashboard-kpi-grid--2 grid-cols-1 sm:grid-cols-2';
    }

    if (this.pageVariant === 'clients') {
      return 'dashboard-kpi-grid--3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3';
    }

    if (this.pageVariant === 'team-performance') {
      return 'dashboard-kpi-grid--5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5';
    }

    if (this.pageVariant === 'revenue') {
      return 'dashboard-kpi-grid--4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-4';
    }

    return 'dashboard-kpi-grid--6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6';
  }

  get pageContentClass(): string {
    if (this.pageVariant === 'overview') {
      return 'dashboard-overview-canvas overflow-y-auto';
    }

    if (this.pageVariant === 'revenue') {
      return 'dashboard-revenue-canvas overflow-y-auto';
    }

    if (this.pageVariant === 'cash-credit-control') {
      return 'dashboard-cash-canvas overflow-y-auto';
    }

    return this.pageVariant === 'team-performance'
      ? 'dashboard-team-canvas overflow-y-auto'
      : 'dashboard-clients-canvas overflow-y-auto';
  }

  get showDashboardCharts(): boolean {
    return this.pageVariant === 'overview' || this.pageVariant === 'revenue' || this.pageVariant === 'cash-credit-control';
  }

  private get kpiSkeletonCount(): number {
    if (this.pageVariant === 'cash-credit-control') return 2;
    if (this.pageVariant === 'clients') return 3;
    if (this.pageVariant === 'revenue' || this.pageVariant === 'team-performance') return 4;
    return 6;
  }

  private kpiKeysForPageVariant(): string[] {
    if (this.pageVariant === 'revenue') {
      return ['nfi', 'gross_profit_margin', 'ebitda_margin', 'net_profit_margin'];
    }

    if (this.pageVariant === 'cash-credit-control') {
      return ['cash_collected_pct', 'aged_debt_90_pct'];
    }

    return [
      'nfi',
      'gross_profit_margin',
      'ebitda_margin',
      'net_profit_margin',
      'debtor_days',
      'cash_collected_pct',
    ];
  }

  private get teamPerformanceKpis(): KpiViewModel[] {
    const summary = this.evertimeDisplaySummary;
    const rpc = summary.revenuePerConsultant;
    const currency = rpc?.currencyCode ?? 'GBP';
    const totalPayout = this.kpis.find(
      kpi => kpi.key === 'total_payout'
    );
    const commissionPayoutRatio = this.kpis.find(
      kpi => kpi.key === 'commission_payout_ratio'
    );

    return [
      this.kpiCard(
        'revenue_per_recruiter',
        'Revenue per Recruiter',
        summary.hasData && rpc ? this.formatKpiCurrency(rpc.revenuePerRecruiter, currency) : '—',
        'pi-chart-line',
        'RPR'
      ),
      this.kpiCard(
        'contractors_worked',
        'Contractors Worked',
        summary.hasData ? this.formatEvertimeCount(summary.contractorsWorked, true) : '—',
        'pi-users',
        'CONT'
      ),
      this.kpiCard(
        'average_contractor_margin',
        'Average Contractor Margin',
        summary.hasData ? this.formatKpiPercent(summary.averageContractorMarginPct) : '—',
        'pi-percentage',
        'MARG'
      ),
      totalPayout ?? this.kpiCard(
        'total_payout',
        'Total Payout',
        '—',
        'pi-wallet',
        'PAY'
      ),
      commissionPayoutRatio ?? this.kpiCard(
        'commission_payout_ratio',
        'Commission Payout Ratio',
        '—',
        'pi-wallet',
        'COMM'
      ),
    ];
  }

  private get clientKpis(): KpiViewModel[] {
    const clientsBilled = this.kpis.find(kpi => kpi.key === 'clients_billed');
    const topClientRevenuePct = this.kpis.find(kpi => kpi.key === 'top_client_revenue_pct');
    const topFiveClientsRevenuePct = this.kpis.find(kpi => kpi.key === 'top_5_clients_revenue_pct');

    return [
      clientsBilled ?? this.kpiCard(
        'clients_billed',
        'Number of Clients Billed',
        '—',
        'pi-briefcase',
        'CLNT'
      ),
      topClientRevenuePct ?? this.kpiCard(
        'top_client_revenue_pct', 'Top Client % of Revenue', '—', 'pi-chart-pie', 'TOP'
      ),
      topFiveClientsRevenuePct ?? this.kpiCard(
        'top_5_clients_revenue_pct', 'Top 5 Clients % of Revenue', '—', 'pi-sitemap', 'TOP5'
      ),
    ];
  }

  kpiByKey(key: string): KpiViewModel | undefined {
    return this.kpis.find(k => k.key === key);
  }

  evertimeHeroKpiValue(key: string): string {
    const kpi = this.kpiByKey(key);
    if (!kpi || kpi.sourceState === 'loading') return '—';
    return kpi.value || '—';
  }

  evertimeHeroKpiCaption(key: string): string {
    const kpi = this.kpiByKey(key);
    if (!kpi || kpi.sourceState === 'loading') return 'Awaiting data';
    if (kpi.sourceState === 'unavailable') return 'No data for period';
    return kpi.note ?? 'Current period';
  }

  evertimeHeroKpiProgress(key: string): number {
    const kpi = this.kpiByKey(key);
    if (!kpi?.value || kpi.value === '—') return 0;
    const numeric = Number(String(kpi.value).replace(/[^\d.-]/g, ''));
    if (!Number.isFinite(numeric)) return 0;
    return Math.max(0, Math.min(100, numeric));
  }

  get invoiceRowCount(): number {
    return this.tableRows?.length ?? 0;
  }

  onRadialSegmentClick(evt: RadialSegmentClick): void {
    this.breakdownSegmentClick.emit(evt.index);
  }

  openContextMenu(
    nativeEvent: MouseEvent,
    source: DashboardContextSource,
    index?: number,
    row?: unknown
  ): void {
    nativeEvent.preventDefault();
    nativeEvent.stopPropagation();

    const menuWidth = 200;
    const menuHeight = 280;
    const padding = 8;

    const maxX = window.innerWidth - menuWidth - padding;
    const maxY = window.innerHeight - menuHeight - padding;

    this.contextMenu = {
      source,
      index,
      row,
      x: Math.max(padding, Math.min(nativeEvent.clientX, maxX)),
      y: Math.max(padding, Math.min(nativeEvent.clientY, maxY))
    };
  }

  closeContextMenu(): void {
    this.contextMenu = null;
    this.overviewMonthContextMenu = null;
  }

  closeOverviewDetail(): void {
    this.overviewDetailIndex = null;
    this.overviewDetailPeriod = 'current';
  }

  isKpiTableEnabled(kpi: KpiViewModel): boolean {
    return this.tableEnabledKpiKeys.has(kpi.key);
  }

  isKpiCardInteractive(kpi: KpiViewModel): boolean {
    return this.isKpiTableEnabled(kpi) ||
      (this.pageVariant === 'overview' && this.overviewMetricForKpi(kpi.key) !== null);
  }

  kpiCardActionLabel(kpi: KpiViewModel): string {
    return this.pageVariant === 'overview' && this.overviewMetricForKpi(kpi.key) !== null
      ? 'Show graph'
      : 'Show as table';
  }

  onKpiCardAction(kpi: KpiViewModel): void {
    const metric = this.pageVariant === 'overview' ? this.overviewMetricForKpi(kpi.key) : null;
    if (!metric) {
      this.openKpiTableDialog(kpi);
      return;
    }

    this.dashboardState.setActiveKpi(kpi.key);
    this.chart1MetricChange.emit(metric);
    setTimeout(() => {
      document.getElementById('overview-relative-graph')?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    });
  }

  private overviewMetricForKpi(key: string): RevenueOverviewMetric | null {
    const metrics: Record<string, RevenueOverviewMetric> = {
      nfi: 'NFI',
      gross_profit_margin: 'gross_profit_margin',
      ebitda_margin: 'ebitda_margin',
      net_profit_margin: 'net_profit_margin',
    };
    return metrics[key] ?? null;
  }

  get kpiTableColumns(): TableColumn[] {
    if (!this.selectedKpiTable) return [];
    if (this.selectedKpiTable.key === 'cash_collected_pct' && this.selectedCashCollectedMonth) {
      return this.cashCollectedTransactionColumns;
    }
    const columns = this.kpiTableColumnsByKey[this.selectedKpiTable.key] ?? [];
    if (this.selectedKpiTable.key === 'contractors_worked' && this.selectedContractorsWorkedPeriod) {
      return [{ header: 'Month Label', field: 'monthLabel', width: '9rem' }, ...columns];
    }
    return columns;
  }

  get selectedConsultantInvoiceTimesheetRows(): EvertimeConsultantInvoiceTimesheetDetailRow[] {
    if (!this.selectedConsultantMonth) return [];
    const consultantName = this.selectedConsultantMonth.consultant.trim().toLocaleLowerCase();
    const monthLabel = this.selectedConsultantMonth.monthLabel.trim().toLocaleLowerCase();
    return this.evertimeDisplaySummary.consultantInvoiceTimesheetDetails.filter(row =>
      row.consultantName.trim().toLocaleLowerCase() === consultantName &&
      row.monthLabel.trim().toLocaleLowerCase() === monthLabel
    );
  }

  get kpiTableRows(): unknown[] {
    if (this.selectedKpiTable?.key === 'contractors_worked') {
      if (this.selectedContractorsWorkedPeriod) {
        return this.evertimeDisplaySummary.contractorsWorkedMonthlyDetails.filter(
          row => this.contractorMonthKey(row.monthLabel) === this.selectedContractorsWorkedPeriod
        );
      }
      return this.evertimeDisplaySummary.contractorsWorkedDetails;
    }
    if (this.selectedKpiTable?.key === 'cash_collected_pct') {
      return this.selectedCashCollectedMonth
        ? this.selectedCashCollectedMonthRow?.transactions ?? []
        : this.cashCollectedMonthRows;
    }
    if (this.selectedKpiTable?.key === 'aged_debt_90_pct') {
      return this.agedDebtOver90DaysDrillDown;
    }
    return [];
  }

  get hasKpiTableComparison(): boolean {
    return !!this.comparisonPeriodLabel && this.selectedKpiTable?.key === 'cash_collected_pct';
  }

  get cashCollectedMonthRows(): CashCollectedLedgerDrillDownRow[] {
    return this.selectedKpiTablePeriod === 'comparison'
      ? this.comparisonCashCollectedLedgerDrillDown
      : this.cashCollectedLedgerDrillDown;
  }

  get selectedCashCollectedMonthRow(): CashCollectedLedgerDrillDownRow | null {
    if (!this.selectedCashCollectedMonth) return null;
    return this.cashCollectedMonthRows.find(row => row.monthLabel === this.selectedCashCollectedMonth) ?? null;
  }

  /** True while the Cash Collected / Ledger popup shows the month list (the drill-down buttons). */
  get showCashCollectedMonthActions(): boolean {
    return this.selectedKpiTable?.key === 'cash_collected_pct' && !this.selectedCashCollectedMonth;
  }

  get kpiTableSummaryLabel(): string {
    if (this.selectedKpiTable?.key === 'cash_collected_pct' && this.selectedCashCollectedMonth) {
      return 'Cash Collected';
    }
    return this.selectedKpiTable?.label ?? '';
  }

  get kpiTableSummaryValue(): string {
    if (this.selectedKpiTable?.key === 'cash_collected_pct' && this.selectedCashCollectedMonth) {
      return this.formatKpiCurrency(this.selectedCashCollectedMonthRow?.monthlyCashCollected);
    }
    if (this.selectedKpiTablePeriod === 'comparison' && this.selectedKpiTable?.comparisonValue) {
      return this.selectedKpiTable.comparisonValue;
    }
    return this.selectedKpiTable?.value ?? '';
  }

  get selectedKpiTablePeriodLabel(): string | null {
    if (!this.hasKpiTableComparison) return null;
    return this.selectedKpiTablePeriod === 'comparison'
      ? this.comparisonPeriodLabel
      : this.dashboardDateRangeLabel;
  }

  get progressionTableColumns(): TableColumn[] {
    if (this.selectedProgressionTableMetric?.key === 'cost_of_sales') {
      return this.costOfSalesProgressionColumns;
    }
    if (this.isWeeklyFinancialMetric(this.selectedProgressionTableMetric?.key)) {
      return this.weeklyFinancialProgressionColumns;
    }
    if (this.selectedProgressionTableMetric?.key === 'operating_cost') {
      return this.administrativeCostProgressionColumns;
    }
    if (this.isFinancialProgressionDetailMetric(this.selectedProgressionTableMetric?.key)) {
      const metricLabel = this.selectedProgressionTableMetric?.name ?? 'Metric';
      return this.financialMetricProgressionColumns.map(column => {
        if (column.field === 'percentage') {
          return { ...column, header: `${metricLabel} Percentage` };
        }
        if (column.field === 'value') {
          return { ...column, header: `${metricLabel} Value` };
        }
        return column;
      });
    }
    return this.revenueProgressionColumns;
  }

  onLiabilitiesMonthChange(month: Date | null): void {
    if (!month) return;
    this.selectedLiabilitiesMonth = month;
    this.loadLiabilities();
  }

  clearLiabilitiesMonth(): void {
    this.selectedLiabilitiesMonth = null;
    this.liabilitiesRequestSequence++;
    this.liabilitiesLoading = false;
    this.liabilitiesError = '';
    this.liabilities = this.liabilities.map(liability => ({ ...liability, amount: 0 }));
  }

  /**
   * Liabilities always open on the current month, so the card shows balances as
   * at today rather than a previous month end or a month left over from an
   * earlier visit. `effectiveLiabilitiesDate()` turns this into today's date.
   */
  private initialLiabilitiesMonth(): Date {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  }

  private loadLiabilities(): void {
    if (!this.selectedLiabilitiesMonth) return;
    const requestSequence = ++this.liabilitiesRequestSequence;
    const date = this.liabilitiesMonthEndApiDate();
    this.liabilitiesLoading = true;
    this.liabilitiesError = '';

    const subscription = this.xeroIntegrationService.getTrialBalance(date).subscribe({
      next: response => {
        if (requestSequence !== this.liabilitiesRequestSequence) return;
        const balances = this.extractTrialBalanceValues(response);
        this.liabilities = this.liabilities.map(liability => ({
          ...liability,
          amount: Math.abs(balances.get(this.normalizeAccountTitle(liability.accountTitle)) ?? 0),
        }));
        this.liabilitiesLoading = false;
      },
      error: () => {
        if (requestSequence !== this.liabilitiesRequestSequence) return;
        this.liabilities = this.liabilities.map(liability => ({ ...liability, amount: 0 }));
        this.liabilitiesLoading = false;
        this.liabilitiesError = 'Data unavailable';
      },
    });
    this.subscriptions.add(subscription);
  }

  private liabilitiesMonthEndApiDate(): string {
    if (!this.selectedLiabilitiesMonth) return '';
    const effectiveDate = this.effectiveLiabilitiesDate();
    const month = String(effectiveDate.getMonth() + 1).padStart(2, '0');
    const day = String(effectiveDate.getDate()).padStart(2, '0');
    return `${effectiveDate.getFullYear()}-${month}-${day}`;
  }

  private effectiveLiabilitiesDate(): Date {
    if (!this.selectedLiabilitiesMonth) return new Date();
    const today = new Date();
    const isCurrentMonth = this.selectedLiabilitiesMonth.getFullYear() === today.getFullYear()
      && this.selectedLiabilitiesMonth.getMonth() === today.getMonth();
    return isCurrentMonth
      ? new Date(today.getFullYear(), today.getMonth(), today.getDate())
      : new Date(
        this.selectedLiabilitiesMonth.getFullYear(),
        this.selectedLiabilitiesMonth.getMonth() + 1,
        0
      );
  }

  private extractTrialBalanceValues(response: unknown): Map<string, number> {
    const targetTitles = new Set(this.liabilities.map(item => this.normalizeAccountTitle(item.accountTitle)));
    const balances = new Map<string, number>();

    const visit = (value: unknown, depth: number): void => {
      if (!value || depth > 12) return;
      if (Array.isArray(value)) {
        value.forEach(item => visit(item, depth + 1));
        return;
      }
      if (typeof value !== 'object') return;

      const record = value as Record<string, unknown>;
      const cells = this.caseInsensitiveValue(record, 'cells');
      if (Array.isArray(cells)) {
        const cellValues = cells.map(cell => {
          if (cell && typeof cell === 'object') {
            return this.caseInsensitiveValue(cell as Record<string, unknown>, 'value');
          }
          return cell;
        });
        const accountTitle = typeof cellValues[0] === 'string'
          ? this.normalizeAccountTitle(cellValues[0])
          : '';
        const matchedTitle = targetTitles.has(accountTitle) ? accountTitle : '';
        if (matchedTitle) {
          // Xero Trial Balance columns are Account, Debit, Credit, YTD Debit, YTD Credit.
          // Liabilities use the YTD Credit balance. Fall back only for older payload shapes.
          const ytdCredit = this.parseTrialBalanceAmount(cellValues[4]);
          const fallbackAmount = cellValues
            .slice(1)
            .map(cell => this.parseTrialBalanceAmount(cell))
            .find(item => item !== null);
          const amount = ytdCredit ?? fallbackAmount;
          if (amount !== undefined && amount !== null) balances.set(matchedTitle, amount);
        }
      }

      const titleValue = ['title', 'accountTitle', 'accountName', 'name', 'label']
        .map(key => this.caseInsensitiveValue(record, key))
        .find(item => typeof item === 'string' && targetTitles.has(this.normalizeAccountTitle(item)));
      if (typeof titleValue === 'string') {
        const normalizedTitle = this.normalizeAccountTitle(titleValue);
        const amount = ['balance', 'closingBalance', 'amount', 'value', 'credit', 'debit']
          .map(key => this.parseTrialBalanceAmount(this.caseInsensitiveValue(record, key)))
          .find(item => item !== null);
        if (amount !== undefined && amount !== null) balances.set(normalizedTitle, amount);
      }

      Object.values(record).forEach(item => visit(item, depth + 1));
    };

    visit(response, 0);
    return balances;
  }

  private caseInsensitiveValue(record: Record<string, unknown>, key: string): unknown {
    const matchingKey = Object.keys(record).find(item => item.toLowerCase() === key.toLowerCase());
    return matchingKey ? record[matchingKey] : undefined;
  }

  private normalizeAccountTitle(value: string): string {
    return value
      .trim()
      // Xero renders the agency-specific account code as a suffix, e.g. "VAT (820)".
      // Remove it from the title before matching; the code itself is never inspected.
      .replace(/\s*\(\d+\)\s*$/, '')
      .replace(/\s+/g, ' ')
      .toLowerCase()
      .replace(/^pensions payable$/, 'pension payable');
  }

  private parseTrialBalanceAmount(value: unknown): number | null {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed || !/[\d]/.test(trimmed)) return null;
    const negative = /^\(.*\)$/.test(trimmed);
    const parsed = Number(trimmed.replace(/[(),£$€\s]/g, '').replace(/,/g, ''));
    return Number.isFinite(parsed) ? (negative ? -parsed : parsed) : null;
  }

  private get activeTurnoverSummary(): TurnoverDrillDownSummary | null { return this.selectedProgressionPeriod === 'comparison' ? this.comparisonTurnoverDrillDownSummary : this.turnoverDrillDownSummary; }
  private get activeTurnoverDetail(): TurnoverDrillDownDetailRow[] { return this.selectedProgressionPeriod === 'comparison' ? this.comparisonTurnoverDrillDownDetail : this.turnoverDrillDownDetail; }
  private get activeCostOfSalesDetail(): CostOfSalesDrillDownDetailRow[] { return this.selectedProgressionPeriod === 'comparison' ? this.comparisonCostOfSalesDrillDownDetail : this.costOfSalesDrillDownDetail; }
  private get activeAdministrativeSummary(): AdministrativeCostDrillDownSummaryRow[] { return this.selectedProgressionPeriod === 'comparison' ? this.comparisonAdministrativeCostDrillDownSummary : this.administrativeCostDrillDownSummary; }
  private get activeAdministrativeDetail(): AdministrativeCostDrillDownDetailRow[] { return this.selectedProgressionPeriod === 'comparison' ? this.comparisonAdministrativeCostDrillDownDetail : this.administrativeCostDrillDownDetail; }
  private get activeFinancialDrillDetail(): FinancialOverviewPoint[] { return this.selectedProgressionPeriod === 'comparison' ? this.comparisonFinancialOverviewDrillDetail : this.financialOverviewDrillDetail; }
  private get activeFinancialDailyDetail(): FinancialOverviewDailyDrillDetailRow[] { return this.selectedProgressionPeriod === 'comparison' ? this.comparisonFinancialOverviewDailyDrillDetail : this.financialOverviewDailyDrillDetail; }

  get progressionTableRows(): unknown[] {
    if (this.selectedProgressionTableMetric?.key === 'cost_of_sales') {
      return this.groupCostOfSalesByMonth(this.activeCostOfSalesDetail);
    }
    const metricKey = this.selectedProgressionTableMetric?.key;
    if (this.isWeeklyFinancialMetric(metricKey)) {
      const sourceRows = this.weeklyFinancialSourceRows(metricKey);
      return this.selectedWeeklyFinancialMonth
        ? this.groupFinancialMetricByWeek(sourceRows.filter(row =>
          this.drillDownPeriod(row.date).monthLabel === this.selectedWeeklyFinancialMonth
        ))
        : this.groupFinancialMetricByMonth(sourceRows);
    }
    if (this.selectedProgressionTableMetric?.key === 'operating_cost') {
      return this.activeAdministrativeSummary.map(row => ({
        ...row,
        drillable: true,
      }));
    }
    if (this.isFinancialProgressionDetailMetric(this.selectedProgressionTableMetric?.key)) {
      const metricKey = this.selectedProgressionTableMetric?.key;
      const detailKeys = this.financialProgressionDetailKeys(metricKey);
      return this.activeFinancialDrillDetail
        .filter(row => !!row.kpiKey && detailKeys.includes(row.kpiKey))
        .map(row => ({
          monthLabel: row.periodLabel,
          percentage: row.kpiPercent,
          value: row.kpiValue,
          drillable: true,
          sourceRow: row,
        }));
    }
    if (this.activeTurnoverSummary?.rows.length) {
      return this.activeTurnoverSummary.rows.map(row => {
        const normalizedKey = row.key.trim().toLowerCase();
        const normalizedName = row.name.trim().toLowerCase();
        const isTotal = normalizedKey === 'total_turnover' || normalizedKey === 'totalturnover'
          || normalizedName === 'total turnover';
        return {
          key: row.key,
          name: row.name,
          value: row.value,
          drillable: !isTotal && this.turnoverDetailsForSummary(row.key, row.name).length > 0,
          highlighted: isTotal,
        };
      });
    }
    return [
      {
        name: this.activeTurnoverSummary?.totalSalesPermLabel ?? '',
        value: this.activeTurnoverSummary?.totalSalesPerm ?? null,
        drillable: true,
        salesType: 'perm',
      },
      {
        name: this.activeTurnoverSummary?.totalSalesTempLabel ?? '',
        value: this.activeTurnoverSummary?.totalSalesTemp ?? null,
        drillable: true,
        salesType: 'temp',
      },
      {
        name: this.activeTurnoverSummary?.totalTurnoverLabel ?? '',
        value: this.activeTurnoverSummary?.totalTurnover ?? null,
        drillable: false,
        salesType: null,
        highlighted: true,
      },
    ];
  }

  get filteredProgressionTableRows(): unknown[] {
    const selectedMonth = this.progressionTableMonthFilter;
    if (!selectedMonth) return this.progressionTableRows;
    return this.progressionTableRows.filter(row =>
      this.monthLabelMatchesDate((row as { monthLabel?: string }).monthLabel, selectedMonth)
    );
  }

  get showProgressionMonthFilter(): boolean {
    const key = this.selectedProgressionTableMetric?.key;
    return key === 'cost_of_sales'
      || (this.isFinancialProgressionDetailMetric(key) && !this.selectedWeeklyFinancialMonth);
  }

  get clientChaseCardHeight(): number {
    const visibleRows = this.clientChasePageSize === 'all'
      ? this.filteredCreditControlClients.length
      : Math.min(this.clientChasePageSize, this.filteredCreditControlClients.length);
    return Math.max(450, 175 + visibleRows * 56);
  }

  onClientChasePageSizeChange(pageSize: number | 'all'): void {
    this.clientChasePageSize = pageSize;
  }

  get filteredSelectedTurnoverDetailRows(): Array<TurnoverMonthlyDetailRow | TurnoverWeeklyDetailRow> {
    const selectedMonth = this.turnoverTableMonthFilter;
    if (!selectedMonth) return this.selectedTurnoverDetailRows;
    return this.selectedTurnoverDetailRows.filter(row =>
      this.monthLabelMatchesDate(row.monthLabel, selectedMonth)
    );
  }

  private monthLabelMatchesDate(monthLabel: string | undefined, selectedMonth: Date): boolean {
    if (!monthLabel) return false;
    const parsed = new Date(`1 ${monthLabel}`);
    return !Number.isNaN(parsed.getTime())
      && parsed.getFullYear() === selectedMonth.getFullYear()
      && parsed.getMonth() === selectedMonth.getMonth();
  }

  get costOfSalesGrandTotalValues(): Record<string, string> | null {
    if (this.selectedProgressionTableMetric?.key !== 'cost_of_sales') return null;
    const rows = this.filteredProgressionTableRows as CostOfSalesMonthlyDetailRow[];
    const totals = rows.reduce(
      (result, row) => ({
        amount: result.amount + row.amount,
        amountPaid: result.amountPaid + row.amountPaid,
      }),
      { amount: 0, amountPaid: 0 }
    );
    return {
      monthLabel: 'Grand Total',
      amount: this.formatKpiCurrency(totals.amount),
      amountPaid: this.formatKpiCurrency(totals.amountPaid),
    };
  }

  get progressionGrandTotalValues(): Record<string, string> | null {
    const costOfSalesTotal = this.costOfSalesGrandTotalValues;
    if (costOfSalesTotal) return costOfSalesTotal;
    const metricKey = this.selectedProgressionTableMetric?.key;
    if (!this.isWeeklyFinancialMetric(metricKey)) return null;
    const total = (this.filteredProgressionTableRows as FinancialOverviewWeeklyDetailRow[])
      .reduce((sum, row) => sum + (row.kpiValue ?? 0), 0);
    return {
      ...(this.selectedWeeklyFinancialMonth
        ? { weekLabel: 'Grand Total' }
        : { monthLabel: 'Grand Total' }),
      kpiValue: this.formatKpiCurrency(total),
    };
  }

  isFinancialProgressionDetailMetric(key: string | null | undefined): boolean {
    return !!key && ['nfi', 'ebitda', 'net_profit'].includes(key);
  }

  isWeeklyFinancialMetric(key: string | null | undefined): key is 'nfi' | 'ebitda' | 'net_profit' {
    return !!key && ['nfi', 'ebitda', 'net_profit'].includes(key);
  }

  get weeklyFinancialProgressionColumns(): TableColumn[] {
    const metricLabel = this.selectedProgressionTableMetric?.name ?? 'Metric';
    return this.selectedWeeklyFinancialMonth
      ? [
        { header: 'Week', field: 'weekLabel', width: '50%' },
        { header: `${metricLabel} Value`, field: 'kpiValue', width: '50%' },
      ]
      : [
        { header: 'Month', field: 'monthLabel', width: '50%' },
        { header: `${metricLabel} Value`, field: 'kpiValue', width: '50%' },
      ];
  }

  private weeklyFinancialSourceRows(
    metricKey: 'nfi' | 'ebitda' | 'net_profit'
  ): FinancialOverviewDailyDrillDetailRow[] {
    const exactRows = this.activeFinancialDailyDetail.filter(row => row.kpiKey === metricKey);
    if (exactRows.length > 0) return exactRows;

    const fallbackKeys = this.financialProgressionDetailKeys(metricKey)
      .filter(key => key !== metricKey);
    return this.activeFinancialDailyDetail.filter(row => fallbackKeys.includes(row.kpiKey));
  }

  private financialProgressionDetailKeys(key: string | null | undefined): string[] {
    if (key === 'ebitda') return ['ebitda', 'ebitda_margin'];
    if (key === 'net_profit') return ['net_profit', 'net_profit_margin'];
    return key ? [key] : [];
  }

  get selectedProgressionKpiCard(): KpiViewModel | null {
    const metric = this.selectedProgressionTableMetric;
    if (!metric) return null;
    const costOfSalesRow = metric.key === 'cost_of_sales'
      ? this.activeCostOfSalesDetail.find(row =>
        row.accountCode || row.accountName || row.accountType || row.accountClass
      )
      : null;
    return {
      key: metric.key,
      label: metric.name,
      value: this.formatKpiCurrency(
        ['cost_of_sales', 'operating_cost'].includes(metric.key) ? Math.abs(metric.value) : metric.value
      ),
      icon: ['cost_of_sales', 'operating_cost'].includes(metric.key)
        ? 'pi-receipt'
        : 'pi-chart-line',
      status: ['cost_of_sales', 'operating_cost'].includes(metric.key)
        ? 'critical'
        : 'positive',
      trendDirection: null,
      note: null,
      sourceState: 'current',
      ribbonLabel: metric.key === 'operating_cost'
        ? 'OPEX'
        : metric.key === 'cost_of_sales'
          ? 'COST'
          : metric.key === 'nfi'
            ? 'NFI'
            : metric.key === 'ebitda'
              ? 'EBITDA'
              : metric.key === 'net_profit' ? 'NET' : 'REV',
      periodLabel: 'Current period',
      details: costOfSalesRow ? [
        { label: 'Account Code', value: costOfSalesRow.accountCode ?? '—' },
        { label: 'Account Name', value: costOfSalesRow.accountName ?? '—' },
        { label: 'Account Type', value: costOfSalesRow.accountType ?? '—' },
        { label: 'Account Class', value: costOfSalesRow.accountClass ?? '—' },
      ] : undefined,
    };
  }

  get selectedTurnoverKpiCard(): KpiViewModel | null {
    if (!this.selectedTurnoverSalesType || !this.selectedTurnoverMetricName) return null;
    const accountCode = this.selectedTurnoverSourceRows.find(row => row.accountCode)?.accountCode ?? '—';
    const accountType = this.selectedTurnoverSourceRows.find(row => row.accountType)?.accountType ?? '—';
    return {
      key: this.selectedTurnoverSalesType,
      label: this.selectedTurnoverMetricName,
      value: this.formatKpiCurrency(this.selectedTurnoverMetricValue),
      icon: 'pi-chart-line',
      status: 'positive',
      trendDirection: null,
      note: null,
      sourceState: 'current',
      ribbonLabel: 'REV',
      periodLabel: 'Current period',
      details: [
        { label: 'Account Code', value: accountCode },
        { label: 'Account Type', value: accountType },
      ],
    };
  }

  get selectedTurnoverGrandTotalValues(): Record<string, string> {
    const totals = this.filteredSelectedTurnoverDetailRows.reduce(
      (result, row) => ({
        amount: result.amount + row.amount,
        amountPaid: result.amountPaid + row.amountPaid,
      }),
      { amount: 0, amountPaid: 0 }
    );
    return {
      monthLabel: 'Grand Total',
      ...(this.selectedTurnoverMonth ? { weekLabel: '' } : {}),
      amount: this.formatKpiCurrency(totals.amount),
      amountPaid: this.formatKpiCurrency(totals.amountPaid),
    };
  }

  openTurnoverSummaryRow(row: { drillable?: boolean; key?: string; name?: string; value?: number }): void {
    if (!row?.drillable || !row.key || !row.name) return;
    const matchingRows = this.turnoverDetailsForSummary(row.key, row.name);
    this.selectedTurnoverSourceRows = matchingRows;
    this.selectedTurnoverDetailRows = this.groupTurnoverByMonth(matchingRows);
    this.selectedTurnoverMonth = null;
    this.turnoverTableMonthFilter = null;
    this.selectedTurnoverSalesType = row.key;
    this.selectedTurnoverMetricName = row.name;
    this.selectedTurnoverMetricValue = row.value ?? null;
  }

  private turnoverDetailsForSummary(key: string, name: string): TurnoverDrillDownDetailRow[] {
    const normalize = (value: string | null | undefined): string =>
      String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
    const summaryKey = normalize(key);
    const summaryKeyWithoutPrefix = summaryKey.replace(/^revenue/, '');
    const summaryName = normalize(name);
    return this.activeTurnoverDetail.filter(row => {
      const candidates = [
        row.summaryKey,
        row.summaryName,
        row.salesType,
        row.accountCode,
        row.accountName,
      ].map(normalize).filter(Boolean);
      return candidates.some(candidate =>
        candidate === summaryKey
        || candidate === summaryKeyWithoutPrefix
        || candidate === summaryName
      );
    });
  }

  openTurnoverDetailRow(row: TurnoverMonthlyDetailRow | TurnoverWeeklyDetailRow): void {
    if (this.selectedTurnoverMonth || !('drillable' in row) || !row.drillable) return;
    this.selectedTurnoverMonth = row.monthLabel;
    this.turnoverTableMonthFilter = null;
    this.selectedTurnoverDetailRows = this.groupTurnoverByMonthWeek(
      this.selectedTurnoverSourceRows.filter(sourceRow =>
        this.drillDownPeriod(sourceRow.date).monthLabel === row.monthLabel
      )
    );
  }

  showTurnoverMonthlySummary(): void {
    this.selectedTurnoverMonth = null;
    this.turnoverTableMonthFilter = null;
    this.selectedTurnoverDetailRows = this.groupTurnoverByMonth(this.selectedTurnoverSourceRows);
  }

  private groupTurnoverByMonth(rows: TurnoverDrillDownDetailRow[]): TurnoverMonthlyDetailRow[] {
    const groups = new Map<string, TurnoverMonthlyDetailRow & { sortKey: string }>();
    for (const row of rows) {
      const period = this.drillDownPeriod(row.date);
      const existing = groups.get(period.sortKey);
      if (existing) {
        existing.amount += row.amount;
        existing.amountPaid += row.amountPaid;
      } else {
        groups.set(period.sortKey, {
          monthLabel: period.monthLabel,
          amount: row.amount,
          amountPaid: row.amountPaid,
          drillable: true,
          sortKey: period.sortKey,
        });
      }
    }
    return [...groups.values()]
      .sort((left, right) => left.sortKey.localeCompare(right.sortKey))
      .map(({ sortKey: _sortKey, ...row }) => row);
  }

  private groupTurnoverByMonthWeek(rows: TurnoverDrillDownDetailRow[]): TurnoverWeeklyDetailRow[] {
    const groups = new Map<string, TurnoverWeeklyDetailRow & { sortKey: string }>();

    for (const row of rows) {
      const period = this.drillDownPeriod(row.date);
      const key = `${period.sortKey}-${period.weekNumber}`;
      const existing = groups.get(key);
      if (existing) {
        existing.amount += row.amount;
        existing.amountPaid += row.amountPaid;
      } else {
        groups.set(key, {
          monthLabel: period.monthLabel,
          weekLabel: `Week ${period.weekNumber}`,
          amount: row.amount,
          amountPaid: row.amountPaid,
          sortKey: key,
        });
      }
    }

    return [...groups.values()]
      .sort((left, right) => left.sortKey.localeCompare(right.sortKey))
      .map(({ sortKey: _sortKey, ...row }) => row);
  }

  private groupCostOfSalesByMonth(rows: CostOfSalesDrillDownDetailRow[]): CostOfSalesMonthlyDetailRow[] {
    const groups = new Map<string, CostOfSalesMonthlyDetailRow & { sortKey: string }>();

    for (const row of rows) {
      const period = this.drillDownPeriod(row.date);
      const existing = groups.get(period.sortKey);
      if (existing) {
        existing.amount += row.amount;
        existing.amountPaid += row.amountPaid;
      } else {
        groups.set(period.sortKey, {
          monthLabel: period.monthLabel,
          amount: row.amount,
          amountPaid: row.amountPaid,
          sortKey: period.sortKey,
        });
      }
    }

    return [...groups.values()]
      .sort((left, right) => left.sortKey.localeCompare(right.sortKey))
      .map(({ sortKey: _sortKey, ...row }) => row);
  }

  private drillDownPeriod(value: string | null): {
    monthLabel: string;
    sortKey: string;
    weekNumber: number;
  } {
    const normalized = value?.trim() ?? '';
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(normalized);
    const parsed = dateOnlyMatch
      ? new Date(Date.UTC(
        Number(dateOnlyMatch[1]),
        Number(dateOnlyMatch[2]) - 1,
        Number(dateOnlyMatch[3])
      ))
      : new Date(normalized);

    if (Number.isNaN(parsed.getTime())) {
      return { monthLabel: 'Unknown month', sortKey: '9999-99', weekNumber: 1 };
    }

    const year = dateOnlyMatch ? parsed.getUTCFullYear() : parsed.getFullYear();
    const monthIndex = dateOnlyMatch ? parsed.getUTCMonth() : parsed.getMonth();
    const day = dateOnlyMatch ? parsed.getUTCDate() : parsed.getDate();
    return {
      monthLabel: new Intl.DateTimeFormat('en-GB', {
        month: 'long',
        year: 'numeric',
        timeZone: dateOnlyMatch ? 'UTC' : undefined,
      }).format(parsed),
      sortKey: `${year}-${String(monthIndex + 1).padStart(2, '0')}`,
      weekNumber: Math.ceil(day / 7),
    };
  }

  openProgressionTableRow(row: any): void {
    if (this.selectedProgressionTableMetric?.key === 'operating_cost') {
      this.openAdministrativeCostDetails(row);
      return;
    }
    if (this.isWeeklyFinancialMetric(this.selectedProgressionTableMetric?.key)) {
      this.openWeeklyFinancialMonth(row);
      return;
    }
    if (this.isFinancialProgressionDetailMetric(this.selectedProgressionTableMetric?.key)) {
      this.openFinancialDailyDetails(row);
      return;
    }
    this.openTurnoverSummaryRow(row);
  }

  openWeeklyFinancialMonth(row: FinancialOverviewMonthlyDetailRow): void {
    if (this.selectedWeeklyFinancialMonth || !row?.drillable || !row.monthLabel) return;
    this.selectedWeeklyFinancialMonth = row.monthLabel;
    this.progressionTableMonthFilter = null;
  }

  showWeeklyFinancialMonthlySummary(): void {
    this.selectedWeeklyFinancialMonth = null;
    this.progressionTableMonthFilter = null;
  }

  private groupFinancialMetricByMonth(
    rows: FinancialOverviewDailyDrillDetailRow[]
  ): FinancialOverviewMonthlyDetailRow[] {
    const groups = new Map<string, FinancialOverviewMonthlyDetailRow & { sortKey: string }>();
    for (const row of rows) {
      const period = this.drillDownPeriod(row.date);
      const existing = groups.get(period.sortKey);
      if (existing) {
        existing.kpiValue += row.kpiValue ?? 0;
      } else {
        groups.set(period.sortKey, {
          monthLabel: period.monthLabel,
          kpiValue: row.kpiValue ?? 0,
          drillable: true,
          sortKey: period.sortKey,
        });
      }
    }
    return [...groups.values()]
      .sort((left, right) => left.sortKey.localeCompare(right.sortKey))
      .map(({ sortKey: _sortKey, ...row }) => row);
  }

  openFinancialDailyDetails(row: { monthLabel?: string; value?: number | null; percentage?: number | null }): void {
    const metricKey = this.selectedProgressionTableMetric?.key;
    const monthLabel = row?.monthLabel;
    if (!metricKey || !monthLabel) return;

    const detailKeys = this.financialProgressionDetailKeys(metricKey);
    const matchingRows = this.activeFinancialDailyDetail.filter(
      detailRow => detailRow.monthLabel === monthLabel && detailKeys.includes(detailRow.kpiKey)
    );
    this.selectedFinancialDailyDetailRows = matchingRows;
    this.selectedFinancialProgressionMonth = monthLabel;
    this.selectedFinancialProgressionRow = {
      monthLabel,
      value: row.value ?? null,
      percentage: row.percentage ?? null,
    };
  }

  private groupFinancialMetricByWeek(rows: FinancialOverviewDailyDrillDetailRow[]): FinancialOverviewWeeklyDetailRow[] {
    const groups = new Map<string, FinancialOverviewWeeklyDetailRow & { sortKey: string }>();
    for (const row of rows) {
      const period = this.drillDownPeriod(row.date);
      const key = `${period.sortKey}-${period.weekNumber}`;
      const existing = groups.get(key);
      if (existing) {
        existing.kpiValue += row.kpiValue ?? 0;
      } else {
        groups.set(key, {
          monthLabel: row.monthLabel,
          weekLabel: `Week ${period.weekNumber}`,
          kpiValue: row.kpiValue ?? 0,
          sortKey: key,
        });
      }
    }
    return [...groups.values()]
      .sort((left, right) => left.sortKey.localeCompare(right.sortKey))
      .map(({ sortKey: _sortKey, ...row }) => row);
  }

  closeFinancialDailyDetailDialog(): void {
    this.selectedFinancialProgressionMonth = null;
    this.selectedFinancialProgressionRow = null;
    this.selectedFinancialDailyDetailRows = [];
  }

  get selectedFinancialMonthKpiCard(): KpiViewModel | null {
    const metric = this.selectedProgressionTableMetric;
    const row = this.selectedFinancialProgressionRow;
    if (!metric || !row) return null;
    return {
      key: metric.key,
      label: metric.name,
      value: this.formatKpiCurrency(row.value),
      icon: 'pi-chart-line',
      status: 'positive',
      trendDirection: null,
      note: null,
      sourceState: 'current',
      ribbonLabel: metric.key === 'net_profit' ? 'NET' : metric.key.toUpperCase(),
      periodLabel: row.monthLabel,
    };
  }

  openAdministrativeCostDetails(row: AdministrativeCostDrillDownSummaryRow): void {
    const selectedName = row.administrativeCostName.trim().toLowerCase();
    this.selectedAdministrativeCostDetailRows = this.activeAdministrativeDetail.filter(
      detailRow => detailRow.administrativeCostName.trim().toLowerCase() === selectedName
    );
    this.selectedAdministrativeCost = row;
  }

  closeAdministrativeCostDetailDialog(): void {
    this.selectedAdministrativeCost = null;
    this.selectedAdministrativeCostDetailRows = [];
  }

  get selectedAdministrativeCostKpiCard(): KpiViewModel | null {
    if (!this.selectedAdministrativeCost) return null;
    return {
      key: 'administrative_cost_detail',
      label: this.selectedAdministrativeCost.administrativeCostName,
      value: this.formatKpiCurrency(Math.abs(this.selectedAdministrativeCost.totalAmount)),
      icon: 'pi-receipt',
      status: 'critical',
      trendDirection: null,
      note: null,
      sourceState: 'current',
      ribbonLabel: 'OPEX',
      periodLabel: 'Current period',
      details: [
        { label: 'Account Code', value: this.selectedAdministrativeCost.accountCode ?? '—' },
      ],
    };
  }

  closeTurnoverDetailDialog(): void {
    this.selectedTurnoverSalesType = null;
    this.selectedTurnoverMetricName = null;
    this.selectedTurnoverMetricValue = null;
    this.selectedTurnoverMonth = null;
    this.selectedTurnoverDetailRows = [];
    this.selectedTurnoverSourceRows = [];
    this.turnoverTableMonthFilter = null;
  }

  openKpiTableDialog(kpi: KpiViewModel): void {
    if (!this.isKpiTableEnabled(kpi)) return;

    const colorIndex = this.kpiSlots.findIndex(item => item.key === kpi.key);
    this.selectedKpiTable = kpi;
    this.selectedKpiTablePeriod = 'current';
    this.selectedCashCollectedMonth = null;
    this.selectedKpiColorIndex = Math.max(0, colorIndex);
  }

  setKpiTablePeriod(period: 'current' | 'comparison'): void {
    this.selectedKpiTablePeriod = period;
    this.selectedCashCollectedMonth = null;
  }

  openCashCollectedMonth(row: CashCollectedLedgerDrillDownRow): void {
    if (!row.drillable) return;
    this.selectedCashCollectedMonth = row.monthLabel;
  }

  showCashCollectedMonths(): void {
    this.selectedCashCollectedMonth = null;
  }

  openContractorsWorkedMonthTable(point: EvertimeContractorTrendPoint): void {
    const kpi = this.kpiSlots.find(item => item.key === 'contractors_worked');
    if (!kpi) return;
    this.selectedContractorsWorkedMonth = point.periodLabel || point.period;
    this.selectedContractorsWorkedPeriod = point.period;
    this.openKpiTableDialog({
      ...kpi,
      value: this.formatEvertimeCount(point.contractorsWorked, true),
      periodLabel: this.selectedContractorsWorkedMonth,
    });
  }

  closeKpiTableDialog(): void {
    this.selectedKpiTable = null;
    this.selectedKpiTablePeriod = 'current';
    this.selectedCashCollectedMonth = null;
    this.selectedContractorsWorkedMonth = null;
    this.selectedContractorsWorkedPeriod = null;
  }

  private contractorMonthKey(monthLabel: string): string {
    const normalized = monthLabel.trim();
    if (/^\d{4}-\d{2}$/.test(normalized)) return normalized;
    const parsed = new Date(`1 ${normalized}`);
    if (Number.isNaN(parsed.getTime())) return normalized.toLocaleLowerCase();
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}`;
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.closeContextMenu();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.closeContextMenu();
  }

  contextDisabled(action: DashboardContextAction): boolean {
    if (!this.contextMenu) {
      return true;
    }

    const source = this.contextMenu.source;

    if (action === 'drillUp') {
      if (source === 'revenue') {
        return this.revenueDrillLevel === 'year';
      }

      if (source === 'cashFlow') {
        return this.activeTrendLevel === 'year';
      }

      if (source === 'breakdown' || source === 'invoice') {
        return !this.activeCrossFilter?.contactName;
      }
    }

    if (action === 'drillDown') {
      if (source === 'revenue') {
        return this.revenueDrillLevel === 'day';
      }

      if (source === 'cashFlow') {
        return this.activeTrendLevel === 'day';
      }

      if (source === 'breakdown') {
        return !!this.activeCrossFilter?.contactName;
      }

      if (source === 'invoice') {
        return true;
      }
    }

    if (action === 'clearFilter') {
      if (source === 'revenue' || source === 'cashFlow') {
        return !(
          this.activeCrossFilter?.year ||
          this.activeCrossFilter?.quarter ||
          this.activeCrossFilter?.month ||
          this.activeCrossFilter?.week ||
          this.activeCrossFilter?.day
        );
      }

      return !this.activeCrossFilter?.contactName;
    }

    return false;
  }

  runContextAction(action: DashboardContextAction): void {
    if (!this.contextMenu || this.contextDisabled(action)) {
      return;
    }

    this.dashboardContextAction.emit({
      source: this.contextMenu.source,
      action,
      index: this.contextMenu.index,
      row: this.contextMenu.row
    });

    this.closeContextMenu();
  }

  openOverviewMonthContextMenu(evt: { dataIndex: number; nativeEvent: MouseEvent }): void {
    if (this.pageVariant !== 'overview') return;
    evt.nativeEvent.preventDefault();
    evt.nativeEvent.stopPropagation();

    const menuWidth = 148;
    const menuHeight = 42;
    const padding = 8;
    this.overviewMonthContextMenu = {
      index: evt.dataIndex,
      x: Math.max(padding, Math.min(evt.nativeEvent.clientX, window.innerWidth - menuWidth - padding)),
      y: Math.max(padding, Math.min(evt.nativeEvent.clientY, window.innerHeight - menuHeight - padding)),
    };
  }

  showOverviewMonthAsTable(): void {
    if (!this.overviewMonthContextMenu) return;
    this.overviewDetailIndex = this.overviewMonthContextMenu.index;
    this.closeContextMenu();
  }

  showAgedDebtAsTable(): void {
    this.closeContextMenu();
    this.agedDebtTableVisible = true;
  }

  closeAgedDebtTableDialog(): void {
    this.agedDebtTableVisible = false;
    this.selectedAgedDebtTableMonth = null;
    this.selectedAgedDebtSummaryMonth = null;
  }

  openAgedDebtMonthDetails(row: AgedDebtBucketSummaryViewRow): void {
    this.selectedAgedDebtSummaryMonth = row.monthLabel;
  }

  closeAgedDebtMonthDetails(): void {
    this.selectedAgedDebtSummaryMonth = null;
  }

  get selectedAgedDebtMonthDetailRows(): AgedDebtBucketDetailViewRow[] {
    if (!this.selectedAgedDebtSummaryMonth) return [];
    return this.agedDebtBucketDetailView.filter(
      row => row.monthLabel === this.selectedAgedDebtSummaryMonth
    );
  }

  openProgressionMetricTable(selection: ProgressionMetricSelection): void {
    const metric = selection.metric;
    this.selectedProgressionPeriod = selection.period;
    this.selectedProgressionTableMetric = metric.key === 'operating_cost'
      ? { ...metric, name: 'Total Administrative Cost' }
      : metric;
    this.progressionTableMonthFilter = null;
    this.selectedWeeklyFinancialMonth = null;
    this.closeContextMenu();
  }

  closeProgressionMetricTableDialog(): void {
    this.selectedProgressionTableMetric = null;
    this.selectedProgressionPeriod = 'current';
    this.progressionTableMonthFilter = null;
    this.selectedWeeklyFinancialMonth = null;
    this.turnoverTableMonthFilter = null;
    this.selectedTurnoverSalesType = null;
    this.selectedTurnoverDetailRows = [];
    this.closeAdministrativeCostDetailDialog();
    this.closeFinancialDailyDetailDialog();
  }

  onRowClick(row: any): void {
    if (!row) {
      return;
    }

    this.selectedInvoice = {
      invoiceNumber: row.invoiceNumber ?? row.number ?? '',
      type: row.type ?? 'ACCREC',
      clientName: row.clientName ?? row.contactName ?? row.name ?? '',
      invoiceDate: row.invoiceDate ?? null,
      dueDate: row.dueDate ?? null,
      status: row.status ?? '',
      currencyCode: row.currencyCode ?? row.currency ?? '',
      reference: row.reference ?? null,
      subTotal: Number(row.subTotal ?? 0),
      taxAmount: Number(row.taxAmount ?? 0),
      total: Number(row.total ?? 0),
      amountPaid: Number(row.amountPaid ?? 0),
      amountCredited: Number(row.amountCredited ?? 0),
      amountDue: Number(row.amountDue ?? 0),
      payments: row.payments ?? [],
      creditNotes: row.creditNotes ?? []
    };

    this.invoiceDialogVisible = true;
  }

  private bindDashboardState(): void {
    this.subscriptions.add(
      this.dashboardState.filterPanelOpen$.subscribe(open => {
        if (!open) {
          return;
        }

        this.slicersPanelVisible = true;
        this.dashboardState.closeFilterPanel();
      })
    );

    this.subscriptions.add(
      this.dashboardState.syncRunning$.subscribe(running => {
        this.syncRunning = running;
      })
    );

    this.subscriptions.add(
      this.dashboardState.syncCompleted$.subscribe(() => {
        this.loadEvertimeSummary();
      })
    );

    this.subscriptions.add(
      this.dashboardState.activeSlicers$.subscribe(slicers => {
        this.selectedRecruiterName = slicers.recruiterName ?? 'all';
      })
    );
  }

  private loadEvertimeSummary(): void {
    const rangeKey = `${this.evertimeStartDate ?? ''}|${this.evertimeEndDate ?? ''}`;
    this.lastEvertimeRangeKey = rangeKey;
    this.evertimeLoadSub?.unsubscribe();

    this.evertimeLoadSub = this.dashboardService
      .getEvertimeSummary(this.evertimeStartDate, this.evertimeEndDate)
      .subscribe({
        next: response => {
          this.evertimeSummary = response;
          this.publishRecruiterOptions(response);
          this.rebuildEvertimeDonutData();
        },
        error: error => {
          console.error('Failed to load Evertime summary', error);
          this.evertimeSummary = { ...this.emptyEvertimeSummary };
          this.evertimeRevenueConcentrationDonutData = [];
        }
      });
    this.subscriptions.add(this.evertimeLoadSub);
  }

  private rebuildEvertimeDonutData(): void {
    const summary = this.evertimeSummary;

    if (!summary) {
      this.evertimeRevenueConcentrationDonutData = [];
      return;
    }

    this.evertimeRevenueConcentrationDonutData = this.buildRevenueConcentrationData(
      summary.topClientsByGrossProfit ?? []
    );
  }

  private publishRecruiterOptions(summary: EvertimeSummary): void {
    const names = new Set<string>();
    (summary.revenuePerConsultant?.breakdown ?? []).forEach(row => {
      if (row.name?.trim()) names.add(row.name.trim());
    });
    (summary.topConsultantsByGrossProfit ?? []).forEach(row => {
      if (row.name?.trim()) names.add(row.name.trim());
    });
    (summary.consultantLeaderboard ?? []).forEach(row => {
      if (row.consultant?.trim()) names.add(row.consultant.trim());
    });
    (summary.contractorsWorkedTrend ?? []).forEach(row => {
      const name = row.consultantName?.trim();
      if (name && name !== 'All Consultants') names.add(name);
    });

    const recruiters = Array.from(names)
      .sort((a, b) => a.localeCompare(b))
      .map(name => ({ label: name, value: name }));

    this.dashboardState.setHeaderFilterOptions({ recruiters });
  }

  private buildRevenueConcentrationData(rows: EvertimeGpRow[]): DonutChartItem[] {
    const visibleNameCounts = new Map<string, number>();

    rows.forEach(row => {
      const name = (row.name ?? '').trim() || 'Unknown client';
      visibleNameCounts.set(name, (visibleNameCounts.get(name) ?? 0) + 1);
    });

    const totals = new Map<string, number>();

    rows.forEach(row => {
      const baseName = (row.name ?? '').trim() || 'Unknown client';
      const hasDuplicateName = (visibleNameCounts.get(baseName) ?? 0) > 1;
      const label = hasDuplicateName && row.currencyCode
        ? `${baseName} (${row.currencyCode})`
        : baseName;

      totals.set(label, (totals.get(label) ?? 0) + (Number(row.revenue) || 0));
    });

    return Array.from(totals.entries())
      .map(([name, value]) => ({ name, value }))
      .filter(item => item.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }

}
